/**
 * Le bonhomme: quatorze segments articules, chacun fusionne en UNE geometrie a
 * couleurs de sommets.
 *
 * Le principe: on assemble des primitives (spheres sculptees, surfaces de
 * revolution, tubes le long de courbes) puis on les fusionne par segment
 * articule. Un segment = un pivot `Object3D` + un seul maillage, donc un seul
 * appel de dessin. Animer = faire tourner les pivots (voir animation.ts).
 * Changer de vetement = reconstruire le ou les segments concernes; changer une
 * couleur = reecrire les plages de l'attribut `color`, sans rien rebatir.
 *
 * Repere: pieds a y = 0 dans `racine`, le bonhomme regarde vers +z.
 */
import * as THREE from 'three'
import { CAM_AZIMUTH, PLAYER_HEIGHT, PLAYER_RADIUS } from '../../shared/constants.ts'
import type { Apparence, Corps } from '../../shared/types/player.ts'
import { validerApparence } from '../../shared/data/apparence.ts'
import { clamp, melangerCouleur, smoothstep } from '../../shared/sim/alea.ts'
import { bosseler, fusionner, tr, type Partie } from '../render/geometrie.ts'

const TAU = Math.PI * 2

// ---------------------------------------------------------------- proportions

const HAUTEUR_BASSIN = 0.75
const TORSE_HAUTEUR = 0.52
const COU = 0.05
/** Deduit pour que le total fasse exactement PLAYER_HEIGHT. */
const TETE_RAYON = (PLAYER_HEIGHT - HAUTEUR_BASSIN - TORSE_HAUTEUR - COU) / 2

/**
 * Table de reglage. Tout ce qui touche a la silhouette est ici. La tete reste
 * volontairement genereuse (environ 4.5 tetes de haut): le bonhomme est vu de
 * loin en vue orthographique a 45 degres et doit rester lisible.
 */
const PROP = {
  hauteurBassin: HAUTEUR_BASSIN,
  torseHauteur: TORSE_HAUTEUR,
  cou: COU,
  teteRayon: TETE_RAYON,
  /** Aplatissement du torse et du bassin selon z: epaules larges, corps mince. */
  torseProfondeur: 0.8,
  /**
   * MEME aplatissement que le torse: plus profond, le bassin obligeait tout
   * haut descendant sur la hanche a s'evaser de cinq pour cent rien que pour
   * le couvrir, ou bien le pantalon ressortait dans son dos.
   */
  bassinProfondeur: 0.8,
  epauleRayon: 0.2,
  tailleRayon: 0.152,
  hancheRayon: 0.165,
  /** Hauteur du pivot d'epaule dans l'espace du torse. */
  epauleY: 0.452,
  /** Epaule vers coude, puis coude vers poignet. */
  brasHaut: 0.26,
  brasBas: 0.25,
  brasRayon: 0.056,
  /** Hanche vers cheville: le pied occupe le reste. */
  jambeLongueur: HAUTEUR_BASSIN - 0.07,
  piedHauteur: 0.07,
  piedLongueur: 0.235,
  piedLargeur: 0.104,
  colRayon: 0.063,
} as const

/** Multiplicateurs de silhouette. Sobre: quelques pourcents suffisent. */
const MORPHO: Record<
  Corps,
  { epaule: number; taille: number; hanche: number; poitrine: number; machoire: number }
> = {
  homme: { epaule: 1.11, taille: 1.0, hanche: 0.92, poitrine: 0.35, machoire: 0.84 },
  femme: { epaule: 0.92, taille: 0.85, hanche: 1.11, poitrine: 1, machoire: 1.14 },
  neutre: { epaule: 1.0, taille: 0.93, hanche: 1.0, poitrine: 0.6, machoire: 1.0 },
}

/** Rayon de la tete, abrege: il sert partout dans le visage. */
const R = PROP.teteRayon
/** Centre de la tete dans l'espace du pivot `tete` (origine au haut du cou). */
const CY = PROP.teteRayon
/**
 * Ligne des sourcils, en y de sphere unite (fois R pour des metres). Le sourcil
 * le plus haut de toutes les variantes d'yeux culmine vers 0.53; on se pose
 * juste au-dessus. C'est le plancher que `bordCheveux` impose DEVANT le visage:
 * regler neuf coiffures a la main ne garantit rien, ce plancher si.
 */
const SOURCIL = 0.55

// -------------------------------------------------------------------- teintes

/** Luminance approximative d'une couleur 0xRRGGBB, dans 0..1. */
function luminance(c: number): number {
  const r = ((c >> 16) & 255) / 255
  const g = ((c >> 8) & 255) / 255
  const b = (c & 255) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Couleur de motif lisible sur `c` (rayures, poches, semelles). */
function motif(c: number): number {
  return luminance(c) > 0.55 ? melangerCouleur(c, 0x1a1d22, 0.58) : melangerCouleur(c, 0xffffff, 0.52)
}

/** Assombrit ou eclaircit selon la clarte, pour que l'ombre reste visible. */
function ombre(c: number, force = 0.22): number {
  return melangerCouleur(c, 0x151821, force)
}

function clair(c: number, force = 0.16): number {
  return melangerCouleur(c, 0xffffff, force)
}

/**
 * Role d'une surface. C'est ce qui remplace les quatorze materiaux d'avant: la
 * couleur est ecrite dans les sommets, et une plage de sommets porte un role.
 * Changer une couleur d'apparence = reecrire les plages des roles touches.
 */
type Role =
  | 'peau'
  | 'peauOmbre'
  | 'peauClair'
  | 'levre'
  | 'bouche'
  | 'cheveux'
  | 'cheveuxClair'
  | 'cheveuxOmbre'
  | 'barbe'
  | 'barbeOmbre'
  | 'cil'
  | 'blanc'
  | 'iris'
  | 'pupille'
  | 'reflet'
  | 'sombre'
  | 'metal'
  | 'haut'
  | 'hautClair'
  | 'hautOmbre'
  | 'hautMotif'
  | 'bas'
  | 'basClair'
  | 'basOmbre'
  | 'basMotif'
  | 'chapeau'
  | 'chapeauOmbre'
  | 'chapeauMotif'
  | 'soulier'
  | 'soulierOmbre'
  | 'semelle'
  | 'accessoire'
  | 'accessoireOmbre'
  | 'accessoireMotif'

function couleurRole(role: Role, a: Apparence): number {
  switch (role) {
    case 'peau':
      return a.peau
    case 'peauOmbre':
      return melangerCouleur(a.peau, 0x3a1f16, 0.3)
    case 'peauClair':
      return melangerCouleur(a.peau, 0xfff3e4, 0.28)
    case 'levre':
      return melangerCouleur(a.peau, 0xa53c46, 0.42)
    case 'bouche':
      return melangerCouleur(a.peau, 0x2a1013, 0.8)
    case 'cheveux':
      return a.cheveuxCouleur
    case 'cheveuxClair':
      return melangerCouleur(a.cheveuxCouleur, 0xffffff, 0.2)
    case 'cheveuxOmbre':
      return melangerCouleur(a.cheveuxCouleur, 0x000000, 0.34)
    case 'barbe':
      return a.barbeCouleur
    case 'barbeOmbre':
      return melangerCouleur(a.barbeCouleur, 0x000000, 0.28)
    case 'cil':
      return melangerCouleur(a.cheveuxCouleur, 0x130e0b, 0.55)
    case 'blanc':
      return 0xf8f4ed
    case 'iris':
      return a.yeuxCouleur
    case 'pupille':
      return melangerCouleur(a.yeuxCouleur, 0x07050a, 0.84)
    case 'reflet':
      return 0xffffff
    case 'sombre':
      return 0x282b31
    case 'metal':
      return 0xb7bec8
    case 'haut':
      return a.hautCouleur
    case 'hautClair':
      return clair(a.hautCouleur)
    case 'hautOmbre':
      return ombre(a.hautCouleur)
    case 'hautMotif':
      return motif(a.hautCouleur)
    case 'bas':
      return a.basCouleur
    case 'basClair':
      return clair(a.basCouleur)
    case 'basOmbre':
      return ombre(a.basCouleur)
    case 'basMotif':
      return motif(a.basCouleur)
    case 'chapeau':
      return a.chapeauCouleur
    case 'chapeauOmbre':
      return ombre(a.chapeauCouleur)
    case 'chapeauMotif':
      return motif(a.chapeauCouleur)
    case 'soulier':
      return a.souliers === 0 ? a.peau : a.souliersCouleur
    case 'soulierOmbre':
      return ombre(a.souliers === 0 ? a.peau : a.souliersCouleur, 0.3)
    case 'semelle':
      return melangerCouleur(a.souliersCouleur, 0x15181d, 0.68)
    case 'accessoire':
      return a.accessoireCouleur
    case 'accessoireOmbre':
      return ombre(a.accessoireCouleur, 0.3)
    case 'accessoireMotif':
      return motif(a.accessoireCouleur)
  }
}

// ------------------------------------------------------- fabrique de primitives

/** Cloche lisse: 1 au centre, 0 au dela de `largeur`. */
function bosse(x: number, centre: number, largeur: number): number {
  const d = Math.abs(x - centre) / largeur
  if (d >= 1) return 0
  const t = 1 - d
  return t * t * (3 - 2 * t)
}

type Profil = readonly (readonly [number, number])[]

/** Surface de revolution autour de Y. `profil` = paires (rayon, y), y croissant. */
function revolution(profil: Profil, seg = 12, phiStart = 0, phiLength = TAU): THREE.LatheGeometry {
  const pts: THREE.Vector2[] = []
  for (const p of profil) pts.push(new THREE.Vector2(Math.max(0.0008, p[0]), p[1]))
  return new THREE.LatheGeometry(pts, seg, phiStart, phiLength)
}

/** Anneau plat epais: un bord de chapeau, une semelle, une ceinture. */
function anneau(rInt: number, rExt: number, y0: number, y1: number, seg = 12): THREE.LatheGeometry {
  return revolution(
    [
      [rInt, y0],
      [rExt, y0],
      [rExt, y1],
      [rInt, y1],
    ],
    seg,
  )
}

function sphere(r: number, seg = 8, anneaux = 6, phi0 = 0, phiL = TAU, th0 = 0, thL = Math.PI) {
  return new THREE.SphereGeometry(r, seg, anneaux, phi0, phiL, th0, thL)
}

function boite(l: number, h: number, p: number, sl = 1, sh = 1, sp = 1): THREE.BoxGeometry {
  return new THREE.BoxGeometry(l, h, p, sl, sh, sp)
}

function capsule(r: number, longueur: number, cap = 2, radial = 6): THREE.CapsuleGeometry {
  return new THREE.CapsuleGeometry(r, Math.max(0.004, longueur - 2 * r), cap, radial, 1)
}

function tore(r: number, tube: number, radial = 4, tubulaire = 10, arc = TAU): THREE.TorusGeometry {
  return new THREE.TorusGeometry(r, tube, radial, tubulaire, arc)
}

/** Tube le long d'une courbe: meche de cheveux, bretelle, echarpe qui pend. */
function courbe(
  points: readonly [number, number, number][],
  r: number,
  segments = 5,
  radial = 4,
): THREE.TubeGeometry {
  const v: THREE.Vector3[] = []
  for (const p of points) v.push(new THREE.Vector3(p[0], p[1], p[2]))
  return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(v), segments, r, radial, false)
}

/**
 * Moyenne les normales des sommets qui occupent le meme point. Sans ca, la
 * couture ou une sphere ou une revolution se referme sur elle-meme apparait
 * comme un trait d'ombre en plein milieu du dos ou du visage.
 */
function souderNormales(geo: THREE.BufferGeometry): void {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute
  const nor = geo.getAttribute('normal') as THREE.BufferAttribute | undefined
  if (!nor) return
  const groupes = new Map<number, number[]>()
  for (let i = 0; i < pos.count; i++) {
    // Cle numerique: une cle texte coutait plus cher que tout le reste du
    // soudage, et `appliquer` est appele a chaque geste dans le createur.
    const qx = Math.round(pos.getX(i) * 8192) + 32768
    const qy = Math.round(pos.getY(i) * 8192) + 32768
    const qz = Math.round(pos.getZ(i) * 8192) + 32768
    const cle = (qx * 65536 + qy) * 65536 + qz
    const liste = groupes.get(cle)
    if (liste) liste.push(i)
    else groupes.set(cle, [i])
  }
  for (const liste of groupes.values()) {
    if (liste.length < 2) continue
    let x = 0
    let y = 0
    let z = 0
    for (const i of liste) {
      x += nor.getX(i)
      y += nor.getY(i)
      z += nor.getZ(i)
    }
    const l = Math.hypot(x, y, z) || 1
    for (const i of liste) nor.setXYZ(i, x / l, y / l, z / l)
  }
  nor.needsUpdate = true
}

/** Deforme une geometrie sommet par sommet, puis recalcule les normales. */
function sculpter<T extends THREE.BufferGeometry>(geo: T, f: (v: THREE.Vector3) => void): T {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute
  const v = new THREE.Vector3()
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i)
    f(v)
    pos.setXYZ(i, v.x, v.y, v.z)
  }
  pos.needsUpdate = true
  geo.computeVertexNormals()
  souderNormales(geo)
  return geo
}

/**
 * Normales radiales depuis un centre. Sur une forme convexe sculptee c'est plus
 * beau que `computeVertexNormals`: parfaitement lisse, et surtout SANS couture
 * visible la ou la sphere se referme sur elle-meme.
 */
function normalesRadiales<T extends THREE.BufferGeometry>(geo: T, cy = 0): T {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute
  const n = new Float32Array(pos.count * 3)
  const v = new THREE.Vector3()
  for (let i = 0; i < pos.count; i++) {
    v.set(pos.getX(i), pos.getY(i) - cy, pos.getZ(i))
    if (v.lengthSq() < 1e-12) v.set(0, 1, 0)
    else v.normalize()
    n[i * 3] = v.x
    n[i * 3 + 1] = v.y
    n[i * 3 + 2] = v.z
  }
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(n, 3))
  return geo
}

// -------------------------------------------- jeu entre la peau et le vetement

/**
 * Marge minimale entre la peau et la coque qui l'habille. Quatre millimetres
 * sur un bonhomme de 1,7 m: invisible a l'oeil, et assez pour qu'aucune
 * facette de peau ne ressorte du tissu.
 */
const JEU_TISSU = 0.004

/** Segments d'une revolution. Le corps et son vetement DOIVENT partager le
 * meme nombre: deux polygones de finesse differente se coupent l'un l'autre. */
const SEG_TRONC = 12
const SEG_MEMBRE = 8

/**
 * Hauteurs ou echantillonner une coque de vetement.
 *
 * Entre deux paliers une surface de revolution coupe DROIT. Une coque qui
 * n'echantillonne pas les memes hauteurs que le corps tend donc sa corde sous
 * les bosses du corps, et la peau ressort par plaques d'un ou deux polygones —
 * exactement le defaut qu'on voyait sur la poitrine, les epaules et les genoux.
 * On reprend donc tous les paliers du corps, plus les siens propres.
 */
function paliersCoque(
  corps: Profil,
  y0: number,
  y1: number,
  pas: number,
  ajouts: readonly number[] = [],
): number[] {
  const ys: number[] = []
  for (const p of corps) if (p[1] > y0 && p[1] < y1) ys.push(p[1])
  for (const y of [...ajouts, ...suite(y0, y1, pas)]) {
    if (y < y0 - 1e-9 || y >= y1) continue
    let proche = false
    for (const a of ys) if (Math.abs(a - y) < 0.01) proche = true
    if (!proche) ys.push(y)
  }
  ys.sort((a, b) => a - b)
  return ys
}

/** Suite arithmetique y0, y0+pas, ... strictement sous y1. */
function suite(y0: number, y1: number, pas: number): number[] {
  const out: number[] = []
  for (let y = y0; y < y1 - 1e-6; y += pas) out.push(y)
  return out
}

/**
 * Hauteurs de la hanche. Une coque de haut qui descend jusque-la s'y evase
 * pour couvrir le bas: sans ces paliers, l'evasement se faisait en deux
 * facettes et le pantalon ressortait entre elles.
 */
const PALIERS_HANCHE = [-0.13, -0.1, -0.07, -0.04, -0.01, 0.02, 0.05]

/** Interpolation lineaire dans un profil (rayon, y) trie par y croissant. */
function rayonEn(profil: Profil, y: number): number {
  const n = profil.length
  if (n === 0) return 0
  const p0 = profil[0] as readonly [number, number]
  if (y <= p0[1]) return p0[0]
  for (let i = 1; i < n; i++) {
    const a = profil[i - 1] as readonly [number, number]
    const b = profil[i] as readonly [number, number]
    if (y <= b[1]) {
      const t = (y - a[1]) / Math.max(1e-6, b[1] - a[1])
      return a[0] + (b[0] - a[0]) * t
    }
  }
  return (profil[n - 1] as readonly [number, number])[0]
}

/**
 * Rayon d'une coque: le corps elargi, mais jamais moins que la peau plus le
 * jeu. C'est ce qui rattrape les rayons ecrits a la main — ourlets, revers,
 * bords replies — qui passaient sous la peau et laissaient un lisere clair.
 */
function rayonCoque(corps: Profil, y: number, marge: number, plus: number): number {
  const r = rayonEn(corps, y)
  return Math.max(r * marge + plus, r + JEU_TISSU)
}

/** Remonte un rayon ecrit a la main juste au-dessus de la peau. */
function horsPeau(corps: Profil, y: number, r: number): number {
  return Math.max(r, rayonEn(corps, y) + JEU_TISSU)
}

/**
 * Deforme une sphere unitaire en crane: machoire qui s'affine vers le menton,
 * menton avance, front bombe, nuque rentree, orbites creusees. `machoire` > 1
 * affine le bas du visage.
 */
function formerTete(v: THREE.Vector3, machoire: number): void {
  const u = clamp(v.y, -1, 1)
  // Largeur: crane large en haut, machoire etroite en bas, tempes plates.
  const fx =
    (u >= 0 ? 1 + 0.03 * bosse(u, 0.45, 0.6) : 1 - 0.3 * machoire * Math.pow(-u, 1.5)) -
    0.03 * bosse(u, 0.3, 0.42)
  // Profondeur: visage un peu plat, front bombe, menton avance, nuque rentree.
  const avant = 0.955 + 0.06 * bosse(u, 0.34, 0.5) + 0.085 * bosse(u, -0.58, 0.44)
  const arriere = 0.94 - 0.09 * Math.max(0, -u - 0.12)
  const fz = arriere + (avant - arriere) * smoothstep(-0.4, 0.4, v.z)
  v.x *= fx
  v.z *= fz
  // Orbites: un creux la ou se posent les yeux, pour enchasser le regard.
  if (v.z > 0.25) {
    const orbite = Math.hypot(Math.abs(v.x) - 0.4, (v.y - 0.12) * 1.35)
    v.z -= 0.075 * bosse(orbite, 0, 0.38) * v.z
    // Pommettes: la rondeur vient du crane, pas d'une boule collee dessus.
    const pom = Math.hypot(Math.abs(v.x) - 0.44, (v.y + 0.16) * 1.5)
    v.z += 0.045 * bosse(pom, 0, 0.44) * v.z
  }
}

// ------------------------------------------------------------------ assemblage

interface Plage {
  role: Role
  debut: number
  nb: number
}

interface Segment {
  maille: THREE.Mesh
  plages: Plage[]
}

/**
 * Collecteur de primitives pour UN segment articule. Il retient, pour chaque
 * primitive, la plage de sommets qu'elle occupera dans la fusion: c'est ce qui
 * permet de reteinter sans reconstruire.
 */
class Assemblage {
  private readonly parties: Partie[] = []
  private readonly plages: Plage[] = []
  private sommets = 0
  private readonly app: Apparence

  constructor(app: Apparence) {
    this.app = app
  }

  get vide(): boolean {
    return this.parties.length === 0
  }

  /** Ajoute une primitive. La geometrie appartient desormais a l'assemblage. */
  ajouter(
    role: Role,
    geo: THREE.BufferGeometry,
    position?: [number, number, number],
    rotation?: [number, number, number],
    echelle?: [number, number, number],
  ): void {
    const index = geo.getIndex()
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined
    const nb = index ? index.count : (pos?.count ?? 0)
    if (nb === 0) {
      geo.dispose()
      return
    }
    this.parties.push({
      geo,
      couleur: couleurRole(role, this.app),
      transforme: tr(position, rotation, echelle),
    })
    const derniere = this.plages[this.plages.length - 1]
    // Deux primitives voisines du meme role tiennent dans une seule plage.
    if (derniere && derniere.role === role) derniere.nb += nb
    else this.plages.push({ role, debut: this.sommets, nb })
    this.sommets += nb
  }

  finir(materiau: THREE.Material): Segment | null {
    if (this.parties.length === 0) return null
    const geo = fusionner(this.parties)
    const maille = new THREE.Mesh(geo, materiau)
    maille.castShadow = true
    return { maille, plages: this.plages }
  }
}

/** Reecrit les couleurs de sommets d'un segment sans toucher a sa geometrie. */
function reteinter(seg: Segment, a: Apparence): void {
  const attr = seg.maille.geometry.getAttribute('color') as THREE.BufferAttribute | undefined
  if (!attr) return
  const c = new THREE.Color()
  for (const p of seg.plages) {
    c.setHex(couleurRole(p.role, a))
    const fin = p.debut + p.nb
    for (let i = p.debut; i < fin; i++) attr.setXYZ(i, c.r, c.g, c.b)
  }
  attr.needsUpdate = true
}

// -------------------------------------------------------------------- segments

const SEGMENTS = [
  'bassin',
  'torse',
  'tete',
  'yeux',
  'brasG',
  'brasD',
  'avantBrasG',
  'avantBrasD',
  'mainG',
  'mainD',
  'jambeG',
  'jambeD',
  'piedG',
  'piedD',
] as const

type CleSegment = (typeof SEGMENTS)[number]

export interface ArticulationsBonhomme {
  bassin: THREE.Object3D
  torse: THREE.Object3D
  tete: THREE.Object3D
  brasG: THREE.Object3D
  brasD: THREE.Object3D
  /** Coudes. Nouveaux: ils donnent un vrai pli au bras au lieu d'un baton. */
  coudeG: THREE.Object3D
  coudeD: THREE.Object3D
  jambeG: THREE.Object3D
  jambeD: THREE.Object3D
  mainG: THREE.Object3D
  mainD: THREE.Object3D
  /** Chevilles. L'animation les contre-tourne pour garder la semelle a plat. */
  piedG: THREE.Object3D
  piedD: THREE.Object3D
}

interface Dimensions {
  epauleR: number
  tailleR: number
  hancheR: number
  poitrineR: number
  epauleX: number
  hancheX: number
  poitrine: number
  machoire: number
  /** Profil (rayon, y) du torse, dans l'espace du pivot `torse`. */
  torse: Profil
  /** Profil (rayon, y) du bassin. */
  bassin: Profil
  /** Profil (rayon, y) d'une jambe, dans l'espace du pivot de hanche. */
  jambe: Profil
}

// ------------------------------------------------------------------- bonhomme

export class Bonhomme {
  readonly racine = new THREE.Group()
  readonly articulations: ArticulationsBonhomme
  /**
   * Copie interne. Elle est mise a jour sur place par `appliquer`: ne la
   * modifie pas depuis l'appelant, lis-la seulement.
   */
  readonly apparence: Apparence
  /** Hauteur du pivot du bassin au repos, utile a l'animation. */
  readonly hauteurBassin = PROP.hauteurBassin
  /**
   * Hanche vers cheville. L'animation en a besoin: sans genou, faire pivoter
   * une jambe lui fait perdre de la hauteur, et il faut abaisser le bassin
   * d'autant pour que le pied reste au sol.
   */
  readonly longueurJambe = PROP.jambeLongueur
  /** Hauteur de la cheville au repos, c'est a dire l'epaisseur du pied. */
  readonly hauteurCheville = PROP.piedHauteur
  /** Les yeux, pour le clignement: un seul maillage, les deux yeux dedans. */
  readonly yeuxMailles: THREE.Object3D[] = []

  private dim: Dimensions
  /** Un seul materiau pour tout le bonhomme: les couleurs sont aux sommets. */
  private readonly materiau = new THREE.MeshLambertMaterial({ vertexColors: true })
  private readonly groupes: Record<CleSegment, THREE.Group>
  private readonly segments: Partial<Record<CleSegment, Segment>> = {}
  /** Raccourcissement courant de chaque jambe, a reappliquer apres un rebati. */
  private facteurJambe = { G: 1, D: 1 }

  constructor(apparence: Apparence) {
    const a = validerApparence(apparence)
    this.apparence = { ...a }
    this.dim = this.calculerDim(a.corps)

    // Hierarchie des pivots.
    const bassin = new THREE.Object3D()
    bassin.position.y = PROP.hauteurBassin
    this.racine.add(bassin)

    const torse = new THREE.Object3D()
    bassin.add(torse)

    const tete = new THREE.Object3D()
    tete.position.y = PROP.torseHauteur + PROP.cou
    torse.add(tete)

    const brasG = new THREE.Object3D()
    brasG.position.set(-this.dim.epauleX, PROP.epauleY, 0)
    const brasD = new THREE.Object3D()
    brasD.position.set(this.dim.epauleX, PROP.epauleY, 0)
    torse.add(brasG, brasD)

    const coudeG = new THREE.Object3D()
    coudeG.position.y = -PROP.brasHaut
    brasG.add(coudeG)
    const coudeD = new THREE.Object3D()
    coudeD.position.y = -PROP.brasHaut
    brasD.add(coudeD)

    const mainG = new THREE.Object3D()
    mainG.position.y = -PROP.brasBas
    coudeG.add(mainG)
    const mainD = new THREE.Object3D()
    mainD.position.y = -PROP.brasBas
    coudeD.add(mainD)

    const jambeG = new THREE.Object3D()
    jambeG.position.set(-this.dim.hancheX, 0, 0)
    const jambeD = new THREE.Object3D()
    jambeD.position.set(this.dim.hancheX, 0, 0)
    bassin.add(jambeG, jambeD)

    const piedG = new THREE.Object3D()
    piedG.position.y = -PROP.jambeLongueur
    jambeG.add(piedG)
    const piedD = new THREE.Object3D()
    piedD.position.y = -PROP.jambeLongueur
    jambeD.add(piedD)

    this.articulations = {
      bassin,
      torse,
      tete,
      brasG,
      brasD,
      coudeG,
      coudeD,
      jambeG,
      jambeD,
      mainG,
      mainD,
      piedG,
      piedD,
    }

    // Un groupe par segment: il porte le maillage fusionne et survit aux
    // reconstructions, donc l'echelle posee par `reglerJambe` ne se perd pas.
    const g = (): THREE.Group => new THREE.Group()
    this.groupes = {
      bassin: g(),
      torse: g(),
      tete: g(),
      yeux: g(),
      brasG: g(),
      brasD: g(),
      avantBrasG: g(),
      avantBrasD: g(),
      mainG: g(),
      mainD: g(),
      jambeG: g(),
      jambeD: g(),
      piedG: g(),
      piedD: g(),
    }
    bassin.add(this.groupes.bassin)
    torse.add(this.groupes.torse)
    tete.add(this.groupes.tete, this.groupes.yeux)
    brasG.add(this.groupes.brasG)
    brasD.add(this.groupes.brasD)
    coudeG.add(this.groupes.avantBrasG)
    coudeD.add(this.groupes.avantBrasD)
    mainG.add(this.groupes.mainG)
    mainD.add(this.groupes.mainD)
    jambeG.add(this.groupes.jambeG)
    jambeD.add(this.groupes.jambeD)
    piedG.add(this.groupes.piedG)
    piedD.add(this.groupes.piedD)

    for (const cle of SEGMENTS) this.batir(cle)
  }

  /**
   * Applique une apparence en ne reconstruisant que les segments touches. Un
   * simple changement de couleur ne reecrit que l'attribut `color`: on peut
   * appeler ceci a chaque mouvement de curseur dans le createur.
   */
  appliquer(a: Apparence): void {
    const p = validerApparence(a)
    const v = this.apparence
    const corps = p.corps !== v.corps

    if (corps) {
      this.dim = this.calculerDim(p.corps)
      const { brasG, brasD, jambeG, jambeD } = this.articulations
      brasG.position.x = -this.dim.epauleX
      brasD.position.x = this.dim.epauleX
      jambeG.position.x = -this.dim.hancheX
      jambeD.position.x = this.dim.hancheX
    }

    // Ce qui commande la FORME de chaque segment. Tout le reste n'est qu'une
    // couleur, donc une reteinte.
    const tete = corps || p.cheveux !== v.cheveux || p.barbe !== v.barbe ||
      p.chapeau !== v.chapeau || p.yeux !== v.yeux || p.accessoire !== v.accessoire
    const torse = corps || p.haut !== v.haut || p.accessoire !== v.accessoire
    const bassin = corps || p.bas !== v.bas || p.accessoire !== v.accessoire
    const bras = corps || p.haut !== v.haut
    const jambe = corps || p.bas !== v.bas || p.souliers !== v.souliers
    const pied = p.souliers !== v.souliers || p.bas !== v.bas
    const main = corps

    Object.assign(this.apparence, p)

    const arebatir: CleSegment[] = []
    if (tete) arebatir.push('tete')
    if (p.yeux !== v.yeux) arebatir.push('yeux')
    if (torse) arebatir.push('torse')
    if (bassin) arebatir.push('bassin')
    if (bras) arebatir.push('brasG', 'brasD', 'avantBrasG', 'avantBrasD')
    if (main) arebatir.push('mainG', 'mainD')
    if (jambe) arebatir.push('jambeG', 'jambeD')
    if (pied) arebatir.push('piedG', 'piedD')

    for (const cle of arebatir) this.batir(cle)
    // Les segments non rebatis peuvent quand meme avoir change de teinte.
    const rebati = new Set<CleSegment>(arebatir)
    for (const cle of SEGMENTS) {
      if (rebati.has(cle)) continue
      const seg = this.segments[cle]
      if (seg) reteinter(seg, this.apparence)
    }
  }

  /**
   * Raccourcit une jambe, `facteur` dans 0.7..1. C'est le genou qu'on n'a pas:
   * l'animation raccourcit la jambe qui se balance pour que le pied decolle du
   * sol, et la laisse a 1 quand elle porte le poids du corps. On deplace la
   * cheville au lieu de mettre une echelle sur l'articulation, sinon le pied
   * serait cisaille par la rotation.
   */
  reglerJambe(gauche: boolean, facteur: number): void {
    if (gauche) this.facteurJambe.G = facteur
    else this.facteurJambe.D = facteur
    this.groupes[gauche ? 'jambeG' : 'jambeD'].scale.y = facteur
    const pied = gauche ? this.articulations.piedG : this.articulations.piedD
    pied.position.y = -PROP.jambeLongueur * facteur
  }

  dispose(): void {
    for (const cle of SEGMENTS) {
      const seg = this.segments[cle]
      if (seg) seg.maille.geometry.dispose()
      delete this.segments[cle]
    }
    this.materiau.dispose()
    this.racine.clear()
    this.yeuxMailles.length = 0
  }

  // ------------------------------------------------------------ construction

  private calculerDim(corps: Corps): Dimensions {
    const m = MORPHO[corps]
    const epauleR = PROP.epauleRayon * m.epaule
    const tailleR = PROP.tailleRayon * m.taille
    const hancheR = PROP.hancheRayon * m.hanche
    const poitrineR = tailleR + (epauleR - tailleR) * 0.74
    return {
      epauleR,
      tailleR,
      hancheR,
      poitrineR,
      epauleX: epauleR * 0.9 + PROP.brasRayon,
      hancheX: hancheR * 0.5,
      poitrine: m.poitrine,
      machoire: m.machoire,
      torse: [
        [tailleR * 0.95, -0.13],
        [tailleR * 1.0, -0.02],
        [tailleR * 0.985, 0.06],
        [tailleR * 1.07, 0.17],
        [poitrineR, 0.28],
        [epauleR * 0.99, 0.38],
        [epauleR, 0.445],
        [epauleR * 0.9, 0.492],
        [epauleR * 0.58, 0.532],
        [0.006, 0.552],
      ],
      // Au-dessus des hanches le bassin RENTRE sous le torse, qui couvre deja
      // la taille: sans ca la coque du bas, plus large que celle du haut au
      // meme endroit, ressortait dans le dos de tous les chandails.
      bassin: [
        [0.006, -0.188],
        [hancheR * 0.7, -0.166],
        [hancheR * 0.93, -0.132],
        [hancheR * 1.02, -0.088],
        [hancheR * 1.0, -0.04],
        [tailleR * 0.88, 0.02],
        [tailleR * 0.84, 0.072],
      ],
      jambe: [
        [0.006, -0.702],
        [0.04, -0.674],
        [0.05, -0.6],
        [0.066, -0.48],
        [0.058, -0.362],
        [0.07, -0.212],
        [0.084, -0.06],
        [0.086, 0.03],
        [0.006, 0.072],
      ],
    }
  }

  /** Reconstruit un segment: nouvelle geometrie fusionnee, meme pivot. */
  private batir(cle: CleSegment): void {
    const as = new Assemblage(this.apparence)
    this.remplir(cle, as)
    const groupe = this.groupes[cle]
    const ancien = this.segments[cle]
    if (ancien) {
      groupe.remove(ancien.maille)
      ancien.maille.geometry.dispose()
      delete this.segments[cle]
    }
    const seg = as.finir(this.materiau)
    if (seg) {
      this.segments[cle] = seg
      groupe.add(seg.maille)
    }
    if (cle === 'yeux') {
      this.yeuxMailles.length = 0
      if (seg) this.yeuxMailles.push(seg.maille)
    }
    if (cle === 'jambeG') groupe.scale.y = this.facteurJambe.G
    if (cle === 'jambeD') groupe.scale.y = this.facteurJambe.D
  }

  private remplir(cle: CleSegment, as: Assemblage): void {
    switch (cle) {
      case 'bassin':
        this.remplirBassin(as)
        break
      case 'torse':
        this.remplirTorse(as)
        break
      case 'tete':
        this.remplirTete(as)
        break
      case 'yeux':
        this.remplirYeux(as)
        break
      case 'brasG':
      case 'brasD':
        this.remplirBras(as, cle === 'brasG')
        break
      case 'avantBrasG':
      case 'avantBrasD':
        this.remplirAvantBras(as, cle === 'avantBrasG')
        break
      case 'mainG':
      case 'mainD':
        this.remplirMain(as, cle === 'mainG')
        break
      case 'jambeG':
      case 'jambeD':
        this.remplirJambe(as, cle === 'jambeG')
        break
      case 'piedG':
      case 'piedD':
        this.remplirPied(as, cle === 'piedG')
        break
    }
  }


  // ------------------------------------------------------------- relief commun

  /**
   * Relief que le torse et les vetements qui l'habillent doivent partager,
   * sinon la poitrine passe a travers la chemise. `ampli` vaut 1 pour la peau
   * et un peu plus pour le tissu, qui se tend par dessus.
   */
  private relieferTorse(v: THREE.Vector3, ampli: number): void {
    const d = this.dim
    if (v.z > 0 && d.poitrine > 0.02) {
      const b =
        bosse(v.y, 0.295, 0.12) * bosse(Math.abs(v.x), d.tailleR * 0.58, d.tailleR * 0.68)
      const g = d.poitrine * 0.06 * b
      v.z += g * ampli
      // Le tassement en Y ne suit PAS `ampli`: si le tissu descendait plus que
      // la peau, il glisserait dessous la ou le profil varie vite.
      v.y -= g * 0.22
    }
    if (v.z < 0) {
      // Omoplates, puis le creux des reins juste en dessous.
      v.z -= 0.014 * ampli * bosse(v.y, 0.34, 0.17)
      v.z += 0.012 * ampli * bosse(v.y, 0.09, 0.11)
    }
    // Un soupcon de pectoral / sternum au centre.
    if (v.z > 0) v.z += 0.008 * ampli * bosse(v.y, 0.26, 0.2) * bosse(v.x, 0, d.tailleR)
  }

  /** Relief partage par le bassin et par le vetement du bas. */
  private relieferBassin(v: THREE.Vector3, ampli: number): void {
    const d = this.dim
    if (v.z < 0) v.z -= 0.055 * ampli * d.hancheR * bosse(v.y, -0.07, 0.15)
    // L'aplatissement de l'entrejambe est le MEME pour la peau et pour le
    // tissu: multiplie par `ampli`, il resserrait le tissu plus vite que le
    // corps et le faisait disparaitre dedans.
    if (v.y < -0.12) v.z *= 0.9
  }

  /**
   * Rayon de la coque du bas. Elle enveloppe le bassin, mais au-dessus des
   * hanches elle passe SOUS le haut: c'est la taille du pantalon, plus large
   * que le chandail au meme endroit, qui ressortait en plaque dans le dos.
   */
  private rayonBas(y: number, marge: number, plus: number): number {
    const peau = rayonEn(this.dim.bassin, y)
    // Sous l'entrejambe la coque revient se poser sur la peau: l'y elargir
    // autant qu'aux hanches lui donnait une bosse en avant, entre les cuisses.
    const ampleur = smoothstep(-0.19, -0.11, y)
    const large = peau * (1 + (marge - 1) * ampleur) + plus * ampleur
    // Coque de haut la plus serree du catalogue, moins la place de la ceinture.
    // 0.019 = le jeu, plus l'epaisseur de la ceinture qui vient se poser dessus.
    const plafond =
      (rayonCoque(this.dim.torse, y, 1.04, 0.01) * PROP.torseProfondeur - 0.019) /
      PROP.bassinProfondeur
    const t = smoothstep(-0.06, -0.02, y)
    return Math.max(peau + JEU_TISSU, large + (Math.min(large, plafond) - large) * t)
  }

  /**
   * Rayon de la coque du haut. Sous la taille elle doit aussi passer PAR-DESSUS
   * le bassin et son vetement: une tunique qui suivait le seul profil du torse
   * laissait ressortir la hanche et le haut du pantalon.
   */
  private rayonHaut(y: number, marge: number, plus: number): number {
    const r = rayonCoque(this.dim.torse, y, marge, plus)
    if (y > 0.09) return r
    // Le bas, ceinture et poches comprises. Les deux coques partagent le meme
    // relief de hanche, la comparaison se fait donc sur les seuls rayons.
    const dos = this.rayonBas(y, 1.06, 0.009) + 0.016 / PROP.torseProfondeur
    // Et la CUISSE habillee: c'est elle qui forme la hanche, et son pantalon
    // ressortait en aileron de chaque cote de tous les hauts un peu longs.
    const cuisse = this.dim.hancheX + this.rayonJambe(y, 1.3, 0.013) + JEU_TISSU
    const sous = Math.max(dos, cuisse)
    return Math.max(r, r + (sous - r) * smoothstep(0.09, 0.03, y))
  }

  /**
   * Point de la SURFACE de la coque du haut, `saillie` metres au-dessus.
   * Boutons, poches, fermetures et bretelles s'y accrochent: poses sur un
   * rayon devine a la main, ils replongeaient sous le tissu.
   */
  private surHaut(
    y: number,
    azimut: number,
    marge: number,
    plus: number,
    saillie: number,
  ): [number, number, number] {
    return this.surRayon(y, azimut, this.rayonHaut(y, marge, plus), saillie)
  }

  /**
   * Meme point, pour un rayon choisi. La bretelle ne suit pas la coque partout:
   * au-dessus du bord du vetement il n'y a plus que la peau, et elle a besoin
   * de la meme mise en relief pour la longer.
   */
  private surRayon(
    y: number,
    azimut: number,
    r: number,
    saillie: number,
  ): [number, number, number] {
    const v = new THREE.Vector3(Math.sin(azimut) * r, y, Math.cos(azimut) * r)
    this.relieferTorse(v, 1.15)
    const z = v.z * PROP.torseProfondeur
    const l = Math.hypot(v.x, z) || 1
    return [v.x + (v.x / l) * saillie, v.y, z + (z / l) * saillie]
  }

  /**
   * Marges de la coque du haut effectivement porte. Ce qui vient PAR-DESSUS
   * (sangles de sac, echarpe) doit s'appuyer dessus, pas sur le torse nu.
   */
  private margesHaut(): [number, number] {
    const table: readonly (readonly [number, number])[] = [
      [1.05, 0.01],
      [1.05, 0.011],
      [1.06, 0.013],
      [1.08, 0.016],
      [1.06, 0.013],
      [1.09, 0.02],
      [1.07, 0.014],
      [1.12, 0.03],
      [1.05, 0.012],
      [1.06, 0.012],
      [1.09, 0.022],
    ]
    return (table[this.apparence.haut] ?? table[1]) as [number, number]
  }

  /**
   * Rayon de la coque d'une jambe. A la hanche elle se resserre sur la cuisse:
   * y garder l'ampleur du bas de jambe la faisait deborder de tous les hauts,
   * en aileron de chaque cote du torse.
   */
  private rayonJambe(y: number, marge: number, plus: number): number {
    const t = smoothstep(-0.02, -0.22, y)
    return rayonCoque(this.dim.jambe, y, 1.06 + (marge - 1.06) * t, 0.008 + (plus - 0.008) * t)
  }

  /**
   * z de la coque du bas a l'abscisse `x`, devant (`sens` = 1) ou derriere.
   * Les poches et les passants s'y accrochent: calcules au centre puis poses
   * sur le cote, ils se retrouvaient deux centimetres derriere le tissu.
   */
  private faceBas(y: number, x: number, marge: number, plus: number, sens: number): number {
    const r = this.rayonBas(y, marge, plus)
    const v = new THREE.Vector3(x, y, sens * Math.sqrt(Math.max(0, r * r - x * x)))
    this.relieferBassin(v, 1.1)
    return v.z * PROP.bassinProfondeur
  }

  /**
   * Ceinture posee SUR la coque du bas, dont elle epouse le galbe. Un simple
   * anneau de rayon constant ressortait en haut, la ou le corps se resserre,
   * et disparaissait dans les reins, la ou la coque se creuse.
   */
  private ceintureBas(marge: number, plus: number, y0: number, y1: number): THREE.LatheGeometry {
    const r = (y: number): number => this.rayonBas(y, marge, plus) + 0.002
    const g = revolution(
      [
        [r(y0), y0],
        [r(y0) + 0.008, y0 + 0.007],
        [r(y1) + 0.008, y1 - 0.007],
        [r(y1), y1],
      ],
      SEG_TRONC,
      Math.PI,
    )
    sculpter(g, (v) => this.relieferBassin(v, 1.1))
    return g
  }

  // ------------------------------------------------------------------- bassin

  private remplirBassin(as: Assemblage): void {
    const os = revolution(this.dim.bassin, SEG_TRONC, Math.PI)
    sculpter(os, (v) => this.relieferBassin(v, 1))
    as.ajouter('peau', os, undefined, undefined, [1, 1, PROP.bassinProfondeur])
    this.bas(as)
    this.accessoireBassin(as)
  }

  /** Coque de vetement suivant le bassin. */
  private culotte(marge: number, plus: number, y0: number): THREE.LatheGeometry {
    const corps = this.dim.bassin
    const profil: [number, number][] = []
    const r = (y: number): number => this.rayonBas(y, marge, plus)
    // Pas d'ourlet replie ici: sous l'entrejambe le corps s'elargit en montant,
    // et une levre rentrante y passe forcement sous la peau — c'est ce qui
    // montrait une plaque de cuisse au bord du vetement.
    profil.push([r(y0), y0])
    for (const y of paliersCoque(corps, y0 + 0.03, 0.068, 0.032)) profil.push([r(y), y])
    profil.push([horsPeau(corps, 0.076, r(0.072) - 0.004), 0.076])
    const g = revolution(profil, SEG_TRONC, Math.PI)
    sculpter(g, (v) => this.relieferBassin(v, 1.1))
    return g
  }

  /** Jupe: cone evase, ourlet epaissi, quelques plis verticaux. */
  private jupe(rHaut: number, rBas: number, y0: number, y1: number, plis: number): THREE.LatheGeometry {
    const profil: [number, number][] = [
      [rBas * 0.9, y0 + 0.012],
      [rBas, y0],
      [rBas * 0.99, y0 + 0.022],
      [rHaut + (rBas - rHaut) * 0.55, y0 + (y1 - y0) * 0.55],
      [rHaut, y1],
    ]
    const g = revolution(profil, 14, Math.PI)
    sculpter(g, (v) => {
      const a = Math.atan2(v.x, v.z)
      const t = smoothstep(y1, y0, v.y)
      const f = 1 + 0.045 * t * Math.cos(a * plis)
      v.x *= f
      v.z *= f
    })
    return g
  }

  private bas(as: Assemblage): void {
    const d = this.dim
    const i = this.apparence.bas
    const ech: [number, number, number] = [1, 1, PROP.bassinProfondeur]

    // La ceinture se porte sur les HANCHES, pas a la taille: plus haut, elle
    // passe sous tous les hauts du catalogue et ressortait par plaques dans
    // leur dos au lieu d'etre cachee ou visible franchement.
    const yC0 = -0.05
    const yC1 = -0.005

    if (i === 4) {
      // Jupe
      as.ajouter('bas', this.culotte(1.04, 0.006, -0.14), undefined, undefined, ech)
      as.ajouter('basOmbre', this.ceintureBas(1.04, 0.006, yC0, yC1), undefined, undefined, ech)
      as.ajouter('bas', this.jupe(d.hancheR * 1.06, d.hancheR * 1.82, -0.24, 0.04, 8))
      return
    }
    if (i === 5) {
      // Robe d'ete: la jupe descend jusqu'aux genoux.
      as.ajouter('bas', this.culotte(1.03, 0.005, -0.14), undefined, undefined, ech)
      as.ajouter('bas', this.jupe(d.hancheR * 1.08, d.hancheR * 2.05, -0.46, 0.04, 10))
      as.ajouter('basMotif', this.ceintureBas(1.03, 0.005, yC0, yC1), undefined, undefined, ech)
      return
    }

    const marge = i === 6 ? 1.03 : 1.06
    const plus = i === 6 ? 0.004 : 0.009
    as.ajouter('bas', this.culotte(marge, plus, -0.16), undefined, undefined, ech)
    // Ceinture, passants et poches: c'est ce qui fait qu'un pantalon existe.
    if (i !== 6) {
      as.ajouter('basOmbre', this.ceintureBas(marge, plus, yC0, yC1), undefined, undefined, ech)
      const yB = (yC0 + yC1) / 2
      // Epaisseur de la ceinture, plus mince que le jeu reserve par le haut:
      // boucle et passants doivent rester dessous quand un chandail les couvre.
      const ep = 0.01 * PROP.bassinProfondeur
      as.ajouter('metal', boite(0.026, 0.02, 0.009), [0, yB, this.faceBas(yB, 0, marge, plus, 1) + ep + 0.002])
      for (const s of [-1, 1]) {
        const xP = s * d.hancheR * 0.5
        as.ajouter('basOmbre', boite(0.014, 0.026, 0.008), [xP, yB, this.faceBas(yB, xP, marge, plus, 1) + ep])
        // Poches arriere: un ton a peine plus clair, pas une etiquette.
        const xQ = s * d.hancheR * 0.52
        as.ajouter('basClair', boite(0.05, 0.046, 0.006), [xQ, -0.1, this.faceBas(-0.1, xQ, marge, plus, -1) - 0.002])
      }
      if (i === 3) {
        // Pantalon de travail: poche a outils sur la cuisse.
        for (const s of [-1, 1]) {
          const xR = s * d.hancheR * 1.0
          as.ajouter('basOmbre', boite(0.05, 0.06, 0.01), [xR, -0.09, this.faceBas(-0.09, xR, marge, plus, 1) + 0.004])
        }
      }
    }
  }

  private accessoireBassin(as: Assemblage): void {
    if (this.apparence.accessoire !== 5) return
    const d = this.dim
    const zz = d.hancheR * PROP.bassinProfondeur
    as.ajouter('accessoire', anneau(d.hancheR * 1.05, d.hancheR * 1.17, 0.0, 0.05, 10), undefined, undefined, [1, 1, PROP.bassinProfondeur + 0.06])
    as.ajouter('metal', boite(0.05, 0.042, 0.014), [0, 0.026, zz * 1.2])
    for (const s of [-1, 1]) {
      const poche = boite(0.062, 0.085, 0.05, 2, 2, 2)
      sculpter(poche, (v) => {
        v.y -= 0.012 * bosse(v.z, 0.025, 0.05)
      })
      as.ajouter('accessoireOmbre', poche, [s * d.hancheR * 1.02, -0.03, zz * 0.45])
      as.ajouter('metal', capsule(0.008, 0.09, 1, 5), [s * d.hancheR * 1.28, -0.02, zz * 0.2], [0.2, 0, s * 0.1])
    }
  }

  // -------------------------------------------------------------------- torse

  private remplirTorse(as: Assemblage): void {
    const d = this.dim
    const pz = PROP.torseProfondeur
    const tronc = revolution(d.torse, SEG_TRONC, Math.PI)
    sculpter(tronc, (v) => this.relieferTorse(v, 1))
    as.ajouter('peau', tronc, undefined, undefined, [1, 1, pz])

    // Cou: il part du haut du torse et rejoint la tete, avec les trapezes.
    const cou = revolution(
      [
        [PROP.colRayon * 1.5, 0.474],
        [PROP.colRayon * 1.12, 0.514],
        [PROP.colRayon * 1.0, 0.556],
        [PROP.colRayon * 0.99, 0.61],
      ],
      10,
      Math.PI,
    )
    sculpter(cou, (v) => {
      v.z *= 0.94
      if (v.z > 0) v.z -= 0.006 * bosse(v.y, 0.6, 0.09)
    })
    as.ajouter('peau', cou, [0, 0, -0.004])

    this.haut(as)
    this.accessoireTorse(as)
  }

  /**
   * Coque de vetement collee au torse. `ferme` la fait monter jusqu'au col;
   * sinon le bord du haut se replie vers l'interieur et laisse voir la peau,
   * ce qui donne une encolure.
   */
  private coqueTorse(
    y0: number,
    y1: number,
    marge: number,
    plus: number,
    ferme: boolean,
    seg = SEG_TRONC,
    pas = 0.1,
  ): THREE.LatheGeometry {
    const corps = this.dim.torse
    const r = (y: number): number => this.rayonHaut(y, marge, plus)
    const profil: [number, number][] = []
    const yb = y0 + 0.03
    // Ourlet: un bourrelet de tissu au bord du bas.
    profil.push([horsPeau(corps, y0 + 0.012, r(y0) + 0.002), y0 + 0.012])
    profil.push([r(y0) + 0.011, y0 + 0.002])
    profil.push([r(yb) + 0.005, yb])
    for (const y of paliersCoque(corps, yb, y1 - 0.03, pas, PALIERS_HANCHE)) {
      if (y > yb + 0.012) profil.push([r(y), y])
    }
    if (ferme) {
      profil.push([r(y1 - 0.02), y1 - 0.02])
      profil.push([r(y1), y1])
    } else {
      profil.push([r(y1), y1])
      profil.push([r(y1) - 0.014, y1 + 0.008])
    }
    const g = revolution(profil, seg, Math.PI)
    sculpter(g, (v) => {
      this.relieferTorse(v, 1.15)
      // Sous la taille la coque epouse aussi la HANCHE, du meme relief que le
      // bas: sans ca elle devait s'evaser partout pour couvrir la seule
      // saillie des fesses.
      this.relieferBassin(v, 1.1)
    })
    return g
  }

  /** Bande horizontale collee au vetement: rayure, ceinture, bordure. */
  private bandeTorse(y: number, hauteur: number, marge: number, plus: number, seg = SEG_TRONC): THREE.LatheGeometry {
    const r0 = this.rayonHaut(y, marge, plus)
    const r1 = this.rayonHaut(y + hauteur, marge, plus)
    // Les bords rentrent de un millimetre, pas d'un pourcentage: a 1.5 % du
    // rayon la bande replongeait sous la coque qu'elle est censee decorer.
    const g = revolution(
      [
        [r0 - 0.001, y],
        [(r0 + r1) / 2 + 0.001, y + hauteur / 2],
        [r1 - 0.001, y + hauteur],
      ],
      seg,
      Math.PI,
    )
    sculpter(g, (v) => this.relieferTorse(v, 1.2))
    return g
  }

  private haut(as: Assemblage): void {
    const d = this.dim
    const pz = PROP.torseProfondeur
    const h = PROP.torseHauteur
    const i = this.apparence.haut
    const hz = (y: number, m = 1.1): number => rayonEn(d.torse, y) * m * pz
    const ech: [number, number, number] = [1, 1, pz]

    const sur = this.surHaut.bind(this)
    /** z de la coque droit devant, la ou se pose la plupart des details. */
    const dev = (y: number, marge: number, plus: number, saillie: number): number =>
      sur(y, 0, marge, plus, saillie)[2]

    /**
     * Bretelle: un ruban qui part du bord AVANT du vetement, epouse l'epaule et
     * redescend au bord ARRIERE. Chaque point de passage est lu sur la surface
     * reelle du corps, du bon cote. Les profondeurs du dos etaient devinees en
     * niant celles du devant, qui n'est pas symetrique: la bretelle plongeait
     * dans l'omoplate puis ressortait a un doigt du dos, en pique vers le ciel.
     */
    const bretelle = (
      s: number,
      ecart: number,
      sommet: number,
      role: Role,
      r = 0.014,
      marge = 1.05,
      plus = 0.01,
    ): void => {
      /**
       * Ce que la bretelle epouse: la coque du vetement tant qu'il y en a, la
       * peau au-dessus de son bord. La coque flotte a plus d'un centimetre du
       * torse; la suivre jusqu'a l'epaule detachait la bretelle de la poitrine
       * nue, qui est justement le seul corps qu'elle ait a toucher la-haut.
       */
      const rayon = (y: number): number => {
        const peau = rayonEn(d.torse, y) + JEU_TISSU
        const coque = this.rayonHaut(y, marge, plus)
        return peau + (coque - peau) * smoothstep(sommet + 0.06, sommet, y)
      }
      const point = (y: number, a: number): THREE.Vector3 =>
        new THREE.Vector3(...this.surRayon(y, a, rayon(y), 0))
      /**
       * Point de passage a l'abscisse `x`, decolle le long de la VRAIE normale.
       * Un ecart radial suffit sur le flanc, mais la pente de l'epaule est
       * presque horizontale: il y enfoncait la bretelle sous la peau au lieu de
       * la poser dessus.
       */
      const pose = (
        y: number,
        x: number,
        sens: number,
        saillie: number,
      ): [number, number, number] => {
        const a0 = Math.asin(clamp(x / rayon(y), -1, 1))
        const a = sens > 0 ? a0 : Math.PI - a0
        const p = point(y, a)
        const n = point(y, a + 0.05)
          .sub(point(y, a - 0.05))
          .cross(point(y + 0.004, a).sub(point(y - 0.004, a)))
          .normalize()
        return [s * (p.x + n.x * saillie), p.y + n.y * saillie, p.z + n.z * saillie]
      }
      /**
       * Crete: le point ou le ruban bascule du devant vers le dos, donc le seul
       * ou il touche la silhouette. On le veut SUR l'epaule, la ou le corps est
       * encore large de moitie en plus que l'ecart. Cherche a la verticale de
       * l'ecart, il tombait contre le cou, la ou le torse n'est plus large que
       * le col: les deux moities du ruban s'y rejoignaient en epingle a cheveux
       * plantee devant la poitrine, au lieu de franchir l'epaule.
       */
      let crete = sommet + 0.04
      for (let y = crete; y <= 0.56; y += 0.001) if (rayon(y) > ecart * 1.5) crete = y
      const xCrete = Math.max(ecart, rayon(crete))
      const bord = Math.min(sommet + 0.02, crete - 0.025)
      /**
       * Une moitie de ruban, du bord du vetement a la crete. Elle finit NOYEE
       * sous le tissu, plus bas que l'ourlet replie du bord: arretee a la
       * surface, la section ouverte du tube se detachait en eclat au milieu du
       * buste, et le ruban semblait tomber de nulle part.
       *
       * L'echantillonnage se resserre vers la crete, et l'abscisse ne s'ecarte
       * qu'en fin de course. Pres de la crete le ruban contourne le flanc a
       * hauteur presque constante: un pas regulier en hauteur franchissait ce
       * virage d'un seul segment, et cette corde tendue en travers de l'epaule
       * est la lame qu'on voyait flotter devant la poitrine.
       */
      const PAS = 8
      const jambe = (sens: number): [number, number, number][] => {
        // Le plongeon sous le tissu se mesure en EPAISSEUR DE RUBAN plus un
        // centimetre, pas en multiple du rayon: proportionnel, il envoyait les
        // grosses bretelles se noyer au coeur du torse, d'ou le ruban ressortait
        // en fine arete au travers du vetement avant de replonger.
        const pts: [number, number, number][] = [
          pose(sommet - 0.03, ecart, sens, -(r + 0.02)),
          pose(sommet + 0.002, ecart, sens, -(r + 0.012)),
          pose(sommet + 0.01, ecart, sens, -r * 0.3),
        ]
        for (let k = 0; k < PAS; k++) {
          const v = 1 - (1 - k / PAS) ** 2
          pts.push(
            pose(
              bord + (crete - bord) * v,
              ecart + (xCrete - ecart) * v * v,
              sens,
              // Le ruban se serre en montant: sur l'epaule il doit mordre dans
              // la chair, alors que sur la poitrine il repose sur le tissu.
              r * (0.5 - 0.3 * v),
            ),
          )
        }
        return pts
      }
      as.ajouter(
        role,
        courbe([...jambe(1), pose(crete, xCrete, 1, r * 0.2), ...jambe(-1).reverse()], r, 48, 6),
      )
    }

    if (i === 0) {
      // Camisole: coque basse, deux bretelles fines.
      as.ajouter('haut', this.coqueTorse(0.0, h * 0.72, 1.05, 0.01, false), undefined, undefined, ech)
      for (const s of [-1, 1]) bretelle(s, d.epauleR * 0.46, h * 0.72, 'haut', 0.012, 1.05, 0.01)
    } else if (i === 1) {
      // Tricot simple: col ras du cou, manches courtes.
      as.ajouter('haut', this.coqueTorse(0.0, 0.552, 1.05, 0.011, true), undefined, undefined, ech)
      as.ajouter('hautMotif', anneau(PROP.colRayon * 1.05, PROP.colRayon * 1.35, 0.5, 0.532, 12))
    } else if (i === 2) {
      // Chemise a carreaux: patte de boutonnage, col ouvert, carreaux suggeres.
      as.ajouter('haut', this.coqueTorse(-0.04, 0.552, 1.06, 0.013, true), undefined, undefined, ech)
      for (const y of [0.08, 0.26, 0.42]) {
        as.ajouter('hautMotif', this.bandeTorse(y, 0.024, 1.06, 0.017), undefined, undefined, ech)
      }
      as.ajouter('hautMotif', boite(0.028, h * 0.92, 0.01), [0, 0.24, dev(0.24, 1.06, 0.013, 0.006)])
      for (let k = 0; k < 4; k++) {
        const y = 0.09 + k * 0.11
        as.ajouter('blanc', sphere(0.008, 4, 3), [0, y, dev(y, 1.06, 0.013, 0.014)])
      }
      this.col(as, 0.5, 0.058, 'haut', 'hautOmbre')
    } else if (i === 3) {
      // Chandail a capuchon: capuche en volume, poche kangourou, cordon.
      as.ajouter('haut', this.coqueTorse(-0.04, 0.552, 1.08, 0.016, true), undefined, undefined, ech)
      const capuche = sphere(0.155, 12, 8)
      sculpter(capuche, (v) => {
        v.z *= 0.82
        if (v.z > 0) v.z *= 0.72
        v.y *= 0.94
      })
      as.ajouter('haut', capuche, [0, 0.5, -d.tailleR * 0.62])
      as.ajouter('hautOmbre', anneau(0.09, 0.145, 0.55, 0.575, 12), [0, 0, -d.tailleR * 0.5], [0.5, 0, 0])
      as.ajouter('hautOmbre', boite(0.17, 0.085, 0.012), [0, 0.11, dev(0.11, 1.08, 0.016, 0.008)])
      for (const s of [-1, 1]) {
        as.ajouter('blanc', capsule(0.006, 0.1, 1, 5), [s * 0.035, 0.46, hz(0.46, 0.95)], [0.25, 0, 0])
      }
    } else if (i === 4) {
      // Salopette: bavette, bretelles croisees dans le dos, boucles.
      as.ajouter('haut', this.coqueTorse(-0.06, h * 0.56, 1.06, 0.013, false), undefined, undefined, ech)
      for (const s of [-1, 1]) {
        bretelle(s, d.epauleR * 0.4, h * 0.56, 'haut', 0.017, 1.06, 0.013)
        as.ajouter('metal', boite(0.026, 0.022, 0.012), [s * d.epauleR * 0.4, 0.27, dev(0.27, 1.06, 0.013, 0.006)])
      }
      as.ajouter('hautMotif', boite(0.1, 0.075, 0.01), [0, 0.16, dev(0.16, 1.06, 0.013, 0.006)])
    } else if (i === 5) {
      // Veste de travail: fermeture, deux poches a rabat, col montant.
      as.ajouter('haut', this.coqueTorse(-0.06, 0.552, 1.09, 0.02, true), undefined, undefined, ech)
      as.ajouter('hautMotif', boite(0.02, h * 0.98, 0.012), [0, 0.24, dev(0.24, 1.09, 0.02, 0.008)])
      for (const s of [-1, 1]) {
        as.ajouter('hautOmbre', boite(0.075, 0.062, 0.012), [s * 0.085, 0.13, dev(0.13, 1.09, 0.02, 0.008)])
        as.ajouter('hautMotif', boite(0.079, 0.014, 0.014), [s * 0.085, 0.166, dev(0.13, 1.09, 0.02, 0.008)])
      }
      this.col(as, 0.5, 0.07, 'haut', 'hautMotif')
    } else if (i === 6) {
      // Tunique: descend sur les hanches, fendue, ceinture de tissu.
      as.ajouter('haut', this.coqueTorse(-0.16, h * 0.86, 1.07, 0.014, false), undefined, undefined, ech)
      // Ruban mince: a 22 mm de rayon la bretelle etait plus epaisse que longue
      // et bossait hors de la silhouette de l'epaule en anse de panier.
      for (const s of [-1, 1]) bretelle(s, d.epauleR * 0.5, h * 0.86, 'haut', 0.015, 1.07, 0.014)
      as.ajouter('hautMotif', this.bandeTorse(0.02, 0.045, 1.09, 0.018), undefined, undefined, ech)
      as.ajouter('hautOmbre', boite(0.03, 0.03, 0.012), [0.02, 0.042, dev(0.04, 1.09, 0.018, 0.008)])
    } else if (i === 7) {
      // Manteau d'hiver: matelasse, col de fourrure, gros boutons.
      as.ajouter('haut', this.coqueTorse(-0.11, 0.552, 1.12, 0.03, true), undefined, undefined, ech)
      for (const y of [-0.03, 0.13, 0.29]) {
        as.ajouter('hautOmbre', this.bandeTorse(y, 0.016, 1.12, 0.034), undefined, undefined, ech)
      }
      const fourrure = tore(0.105, 0.042, 3, 10)
      bosseler(fourrure, 0.16, 7)
      as.ajouter('hautMotif', fourrure, [0, 0.545, 0], [Math.PI / 2, 0, 0], [1, 1, 0.9])
      as.ajouter('hautMotif', boite(0.022, h * 0.9, 0.014), [0, 0.2, dev(0.2, 1.12, 0.03, 0.012)])
    } else if (i === 8) {
      // Chandail raye
      as.ajouter('haut', this.coqueTorse(-0.02, 0.552, 1.05, 0.012, true), undefined, undefined, ech)
      for (let k = 0; k < 4; k++) {
        as.ajouter('hautMotif', this.bandeTorse(0.03 + k * 0.115, 0.045, 1.05, 0.015), undefined, undefined, ech)
      }
      as.ajouter('hautMotif', anneau(PROP.colRayon * 1.05, PROP.colRayon * 1.4, 0.498, 0.53, 12))
    } else if (i === 9) {
      // Blouse fleurie: encolure ronde, volant a l'ourlet, petites fleurs.
      as.ajouter('haut', this.coqueTorse(-0.03, h * 0.9, 1.06, 0.012, false), undefined, undefined, ech)
      for (const s of [-1, 1]) bretelle(s, d.epauleR * 0.48, h * 0.9, 'haut', 0.015, 1.06, 0.012)
      as.ajouter('hautMotif', this.bandeTorse(-0.03, 0.03, 1.09, 0.016), undefined, undefined, ech)
      for (let k = 0; k < 9; k++) {
        const a = (k / 9) * TAU + 0.3
        const y = 0.04 + (k % 3) * 0.14
        as.ajouter('hautMotif', sphere(0.017, 4, 3), sur(y, a, 1.06, 0.012, 0.005))
      }
    } else {
      // Chemisier ouvert sur un tricot: deux pans plus un col.
      // Un tricot dessous, un chemisier ouvert dessus: la coque du dessus ne
      // descend que jusqu'a la poitrine, et deux revers filent vers l'ourlet.
      as.ajouter('hautMotif', this.coqueTorse(-0.02, 0.552, 1.04, 0.01, true, 12, 0.2), undefined, undefined, ech)
      as.ajouter('haut', this.coqueTorse(0.2, 0.552, 1.09, 0.022, true, 12, 0.14), undefined, undefined, ech)
      for (const s of [-1, 1]) {
        as.ajouter(
          'haut',
          courbe(
            [
              [s * 0.075, 0.48, hz(0.46, 0.72)],
              [s * 0.075, 0.34, hz(0.34, 1.1)],
              [s * 0.085, 0.16, hz(0.16, 1.16)],
              [s * 0.095, -0.03, hz(0.0, 1.14)],
            ],
            0.026,
            5,
            4,
          ),
        )
      }
      this.col(as, 0.5, 0.062, 'haut', 'hautOmbre')
    }
  }

  /** Col replie autour du cou: deux pointes devant, une bande derriere. */
  private col(as: Assemblage, y: number, taille: number, role: Role, roleInterieur: Role): void {
    const rc = PROP.colRayon
    as.ajouter(
      roleInterieur,
      revolution(
        [
          [rc * 1.06, y],
          [rc * 1.5, y + taille * 0.75],
          [rc * 1.42, y + taille],
        ],
        12,
        Math.PI * 0.35,
        Math.PI * 1.3,
      ),
      undefined,
      undefined,
      [1, 1, 0.92],
    )
    for (const s of [-1, 1]) {
      const pointe = boite(0.05, 0.012, 0.055, 1, 1, 2)
      sculpter(pointe, (v) => {
        if (v.z > 0) v.x *= 0.35
      })
      as.ajouter(role, pointe, [s * 0.038, y + taille * 0.35, rc * 1.15], [-0.5, s * 0.35, 0])
    }
  }

  private accessoireTorse(as: Assemblage): void {
    const d = this.dim
    const i = this.apparence.accessoire
    // Un accessoire passe PAR-DESSUS le vetement porte, jamais dedans.
    const mp = this.margesHaut()
    const dessus = (y: number, x: number, saillie: number): number => {
      const r = this.rayonHaut(y, mp[0], mp[1])
      return this.surHaut(y, Math.asin(clamp(x / r, -1, 1)), mp[0], mp[1], saillie)[2]
    }

    if (i === 1) {
      // Sac a dos: corps arrondi, rabat, sangles qui passent sur les epaules.
      const zDos = -d.tailleR * PROP.torseProfondeur - 0.085
      const sac = boite(0.24, 0.3, 0.15, 2, 2, 2)
      sculpter(sac, (v) => {
        const f = 1 - 0.1 * bosse(Math.abs(v.y), 0.15, 0.2)
        v.x *= f
        v.z *= 1 - 0.12 * bosse(Math.abs(v.x), 0.12, 0.16)
        if (v.z < -0.06) v.z -= 0.02 * bosse(v.y, 0, 0.16)
        v.y *= f
      })
      as.ajouter('accessoire', sac, [0, 0.3, zDos])
      as.ajouter('accessoireOmbre', boite(0.245, 0.075, 0.155, 1, 1, 1), [0, 0.42, zDos - 0.004])
      as.ajouter('accessoireMotif', boite(0.09, 0.06, 0.012), [0, 0.24, zDos - 0.08])
      as.ajouter('metal', boite(0.03, 0.018, 0.012), [0, 0.375, zDos - 0.082])
      for (const s of [-1, 1]) {
        as.ajouter(
          'accessoireOmbre',
          courbe(
            [
              [s * 0.09, 0.12, -dessus(0.12, 0.09, 0.012)],
              [s * 0.1, 0.32, dessus(0.32, 0.1, 0.014)],
              [s * 0.1, 0.46, dessus(0.44, 0.1, 0.014) * 0.6],
              [s * 0.095, 0.47, -0.02],
              [s * 0.09, 0.4, zDos + 0.06],
            ],
            0.017,
            6,
            4,
          ),
        )
      }
    } else if (i === 3) {
      // Echarpe: un tour de cou et un pan qui retombe devant.
      const boucle = tore(0.098, 0.036, 4, 12)
      bosseler(boucle, 0.14, 3)
      as.ajouter('accessoire', boucle, [0, 0.505, 0], [Math.PI / 2, 0, 0], [1, 1, 0.92])
      as.ajouter(
        'accessoire',
        courbe(
          [
            [0.045, 0.51, dessus(0.5, 0.045, 0.02) * 0.7],
            [0.06, 0.4, dessus(0.4, 0.06, 0.03)],
            [0.055, 0.28, dessus(0.28, 0.055, 0.032)],
            [0.04, 0.16, dessus(0.2, 0.04, 0.032)],
          ],
          0.028,
          5,
          4,
        ),
      )
      as.ajouter('accessoireMotif', boite(0.062, 0.02, 0.05), [0.04, 0.15, dessus(0.2, 0.04, 0.032)])
    } else if (i === 4) {
      // Cape: pan de tissu ouvert devant, avec des plis et une agrafe.
      const cape = revolution(
        [
          [d.epauleR * 1.6, -0.21],
          [d.epauleR * 1.3, 0.06],
          [d.epauleR * 1.14, 0.32],
          [d.epauleR * 1.06, 0.46],
          [d.epauleR * 0.7, 0.53],
        ],
        14,
        Math.PI * 0.34,
        Math.PI * 1.32,
      )
      sculpter(cape, (v) => {
        const a = Math.atan2(v.x, v.z)
        const t = smoothstep(0.5, -0.2, v.y)
        const f = 1 + 0.05 * t * Math.cos(a * 7)
        v.x *= f
        v.z *= f * 0.95
      })
      as.ajouter('accessoire', cape, [0, 0, -0.01])
      as.ajouter('accessoireMotif', boite(0.1, 0.026, 0.026), [0, 0.52, d.tailleR * 0.8])
      as.ajouter('metal', sphere(0.014, 6, 5), [0, 0.52, d.tailleR * 0.95])
    }
  }

  // --------------------------------------------------------------------- tete

  /** Forme des yeux selon la variante choisie. Partagee tete <-> yeux. */
  private formeYeux(): { r: number; ay: number; ex: number; dy: number; ir: number; ez: number } {
    const formes: readonly { r: number; ay: number; ex: number; dy: number; ir: number }[] = [
      { r: 0.05, ay: 1.0, ex: 0.072, dy: 0.016, ir: 0.028 },
      { r: 0.054, ay: 0.72, ex: 0.074, dy: 0.016, ir: 0.026 },
      { r: 0.05, ay: 0.62, ex: 0.073, dy: 0.026, ir: 0.03 },
      { r: 0.058, ay: 1.02, ex: 0.077, dy: 0.012, ir: 0.034 },
      { r: 0.052, ay: 0.5, ex: 0.073, dy: 0.006, ir: 0.026 },
      { r: 0.048, ay: 0.58, ex: 0.075, dy: 0.022, ir: 0.03 },
    ]
    const f = formes[this.apparence.yeux] ?? (formes[0] as { r: number; ay: number; ex: number; dy: number; ir: number })
    return { ...f, ez: 0.148 }
  }

  /**
   * Volume pose sur le crane (cheveux, barbe, coiffe de chapeau). `densite`
   * donne, pour une direction sur la tete, l'epaisseur locale: 0 laisse le
   * volume SOUS la peau donc invisible, 1 donne une meche pleine. C'est ce qui
   * remplace la calotte d'avant, et ce qui permet une vraie ligne de cheveux.
   */
  private volumeTete(
    densite: (x: number, y: number, z: number) => number,
    epais = 0.115,
    seg = 14,
    anneaux = 12,
  ): THREE.BufferGeometry {
    const g = sphere(1, seg, anneaux)
    const pos = g.getAttribute('position') as THREE.BufferAttribute
    const v = new THREE.Vector3()
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i)
      formerTete(v, this.dim.machoire)
      v.multiplyScalar(0.945 + epais * clamp(densite(v.x, v.y, v.z), 0, 1.8))
      pos.setXYZ(i, v.x, v.y, v.z)
    }
    pos.needsUpdate = true
    return normalesRadiales(g)
  }

  /** Coiffe rigide (chapeau) epousant le crane, ouverte en bas. */
  private dome(couvre: number, seg = 12, anneaux = 7): THREE.BufferGeometry {
    const g = sphere(1, seg, anneaux, 0, TAU, 0, Math.PI * couvre)
    const pos = g.getAttribute('position') as THREE.BufferAttribute
    const v = new THREE.Vector3()
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i)
      formerTete(v, this.dim.machoire * 0.6)
      pos.setXYZ(i, v.x, v.y, v.z)
    }
    pos.needsUpdate = true
    return normalesRadiales(g)
  }

  private remplirTete(as: Assemblage): void {
    const a = this.apparence
    const f = this.formeYeux()

    // Raccord de cou: il comble le jeu quand la tete tourne ou s'incline.
    as.ajouter(
      'peau',
      revolution(
        [
          [PROP.colRayon * 1.06, -0.075],
          [PROP.colRayon * 1.0, -0.02],
          [PROP.colRayon * 0.92, 0.025],
        ],
        10,
        Math.PI,
      ),
      [0, 0, -0.004],
    )

    // Crane sculpte: machoire, menton, front, nuque, orbites.
    const crane = sphere(1, 14, 11)
    const pos = crane.getAttribute('position') as THREE.BufferAttribute
    const v = new THREE.Vector3()
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i)
      formerTete(v, this.dim.machoire)
      pos.setXYZ(i, v.x, v.y, v.z)
    }
    pos.needsUpdate = true
    normalesRadiales(crane)
    as.ajouter('peau', crane, [0, CY, 0], undefined, [R, R, R])

    // Oreilles: un pavillon en capsule (donc arrondi aux deux bouts, alors
    // qu'une sphere etiree finissait en pointe) et une conque plus sombre.
    for (const s of [-1, 1]) {
      as.ajouter('peau', capsule(0.024, 0.072, 2, 5), [s * R * 1.0, CY + 0.008, -0.012], [0, 0, s * -0.16], [0.5, 1, 0.92])
      // La conque affleure le pavillon: posee plus dehors, elle formait un eclat
      // sombre detache, flottant devant la tempe.
      as.ajouter('peauOmbre', sphere(0.024, 5, 4), [s * R * 1.045, CY + 0.004, -0.008], undefined, [0.24, 1.1, 0.7])
    }

    // Nez: arete, bout legerement retrousse, deux narines.
    as.ajouter('peau', capsule(0.012, 0.075, 1, 5), [0, CY + 0.016, 0.158], [-0.34, 0, 0])
    as.ajouter('peau', sphere(0.023, 7, 5), [0, CY - 0.03, 0.178], undefined, [1.5, 0.95, 1.0])
    for (const s of [-1, 1]) {
      as.ajouter('peauOmbre', sphere(0.009, 4, 3), [s * 0.019, CY - 0.044, 0.176], undefined, [1, 0.55, 0.8])
    }

    // Bouche: deux levres qui se TOUCHENT le long d'une seule couture. Elles
    // suivent la COURBE du visage — un tore plat decollait aux commissures et
    // flottait a un centimetre de la joue — et surtout elles partagent le meme
    // affaissement et la meme enveloppe: l'ecart des levres et leur epaisseur
    // s'annulent ENSEMBLE aux deux bouts, donc la bouche se ferme en
    // pointe au lieu de finir par deux boudins ronds. La couture est plus
    // ETROITE que les levres: plus large, elle depassait des commissures et la
    // silhouette n'etait qu'une barre sombre avec deux bosses.
    const yb = CY - 0.084
    const DEMI = 0.038
    // L'affaissement depend de x et non du parametre de chaque courbe: sinon
    // les trois s'affaissent chacune a son rythme et la couture traverse les
    // levres au lieu de se loger entre elles.
    const creux = (x: number): number => -0.006 * Math.sin(Math.PI * clamp((x / DEMI + 1) / 2, 0, 1))
    const ligne = (
      role: Role,
      demi: number,
      ecart: number,
      rayon: number,
      dz: number,
      arc: number,
      radial: number,
      mince: number,
    ): void => {
      const pas = 8
      const enveloppe = (t: number): number => Math.sin(Math.PI * t) ** 0.55
      // Arc de Cupidon: deux petits sommets de part et d'autre du philtrum.
      const axeY = (t: number): number =>
        yb +
        creux(demi * (2 * t - 1)) +
        enveloppe(t) * (ecart + arc * (bosse(t, 0.34, 0.22) + bosse(t, 0.66, 0.22)))
      const pts: [number, number, number][] = []
      for (let k = 0; k <= pas; k++) {
        const t = k / pas
        const x = demi * (2 * t - 1)
        const y = axeY(t)
        pts.push([x, y, this.zVisage(x, y) + dz])
      }
      const g = courbe(pts, rayon, pas, radial)
      sculpter(g, (v) => {
        const t = clamp((v.x / demi + 1) / 2, 0, 1)
        const y = axeY(t)
        const z = this.zVisage(v.x, y) + dz
        // Le tube s'amincit vers les commissures. A rayon constant la bouche
        // gardait la meme epaisseur d'un bout a l'autre, ce qui se lit comme un
        // trait colle sur le visage.
        const f = mince + (1 - mince) * Math.sin(Math.PI * t) ** 0.45
        v.y = y + (v.y - y) * f
        v.z = z + (v.z - z) * f
      })
      as.ajouter(role, g)
    }
    ligne('levre', 0.038, 0.0125, 0.0108, 0.0015, 0.0038, 6, 0.16)
    ligne('bouche', 0.031, 0, 0.0042, 0.0035, 0, 4, 0.5)
    ligne('levre', 0.036, -0.0155, 0.0125, 0.001, 0, 6, 0.16)

    // Sourcils: leger arc, incline selon la variante des yeux.
    const pente = [0.05, 0.09, -0.11, 0.035, 0.16, -0.26][a.yeux] ?? 0.07
    const yS = CY + f.dy + f.r * f.ay + 0.021
    for (const s of [-1, 1]) {
      const pts: [number, number, number][] = []
      for (let k = 0; k <= 3; k++) {
        const t = k / 3
        const x = s * (f.ex - 0.038 + 0.078 * t)
        const y = yS + 0.007 * Math.sin(Math.PI * t) + pente * (t - 0.5) * 0.075
        pts.push([x, y, this.zVisage(x, y) + 0.006])
      }
      as.ajouter(a.cheveux === 0 ? 'barbe' : 'cheveux', courbe(pts, 0.0092, 3, 4))
    }

    // Paupiere: un repli de peau et sa ligne de cils, au bord haut du globe.
    for (const s of [-1, 1]) {
      const bord = (dy: number, rayon: number, role: Role, dz: number): void => {
        const pts: [number, number, number][] = []
        for (let k = 0; k <= 3; k++) {
          const t = k / 3
          const x = s * (f.ex - f.r * 1.02 + f.r * 2.04 * t)
          const y = CY + f.dy + dy - f.r * f.ay * 0.3 * (2 * t - 1) ** 2
          pts.push([x, y, this.zVisage(x, y) + dz])
        }
        as.ajouter(role, courbe(pts, rayon, 3, 4))
      }
      bord(f.r * f.ay * 0.62, 0.006, 'cil', 0.013)
      bord(f.r * f.ay * 0.62 + 0.014, 0.009, 'peauClair', 0.006)
    }

    this.cheveux(as)
    this.barbe(as)
    this.chapeau(as)
    if (a.accessoire === 2) this.lunettes(as)
  }

  private remplirYeux(as: Assemblage): void {
    const f = this.formeYeux()
    this.groupes.yeux.position.set(0, CY + f.dy, 0)
    for (const s of [-1, 1]) {
      // Chaque oeil est une lentille INCLINEE vers l'exterieur: elle epouse la
      // rondeur du crane au lieu de sortir du visage par le coin exterieur.
      const lacet = s * 0.34
      const nx = Math.sin(lacet)
      const nz = Math.cos(lacet)
      const rot: [number, number, number] = [0, lacet, 0]
      const p = (d: number, dx = 0, dy = 0): [number, number, number] => [
        s * f.ex + nx * d + dx,
        dy,
        f.ez + nz * d,
      ]
      as.ajouter('blanc', sphere(f.r, 7, 4), p(0), rot, [1, f.ay, 0.34])
      as.ajouter('iris', sphere(f.ir, 5, 4), p(f.r * 0.2), rot, [1, Math.max(0.55, f.ay), 0.4])
      as.ajouter('pupille', sphere(f.ir * 0.46, 4, 3), p(f.r * 0.27), rot, [1, 1, 0.7])
      as.ajouter('reflet', sphere(f.ir * 0.32, 4, 3), p(f.r * 0.29, -s * 0.009, f.ir * 0.42), rot, [1, 1, 0.7])
    }
  }

  /**
   * z approximatif de la surface du visage au point (x, y). Sert a poser la
   * bouche, les sourcils et les cils SUR le visage, pas devant.
   */
  private zVisage(x: number, y: number): number {
    const v = new THREE.Vector3(x / R, (y - CY) / R, 1)
    v.z = Math.sqrt(Math.max(0.04, 1 - v.x * v.x - v.y * v.y))
    formerTete(v, this.dim.machoire)
    return v.z * R + CY * 0
  }

  // ------------------------------------------------------------------ cheveux

  /**
   * Ce que le chapeau porte laisse de place aux cheveux.
   *
   * `epaisseur`: epaisseur de chevelure admise, en fraction du rayon de tete.
   * `bord`: hauteur du bas de la calotte, en y de sphere unite; EN DESSOUS la
   * chevelure reste libre, sinon une queue de cheval disparaitrait avec la
   * casquette au lieu d'en sortir.
   *
   * Les rayons viennent de `chapeau()`, moins le jeu peau-tissu. Toutes les
   * calottes fermees sont plus serrees que la chevelure qu'elles couvrent — la
   * plus genereuse, le casque de chantier, l'est encore moins que l'afro — donc
   * c'est bien aux cheveux de se plaquer, pas au chapeau de gonfler.
   *
   * `Infinity` = tete nue, ou calotte OUVERTE (couronne, bandeau): les cheveux
   * doivent passer au travers, c'est tout l'interet de ces deux-la.
   */
  private placeSousChapeau(): { epaisseur: number; bord: number } {
    const calottes: Record<number, readonly [number, number]> = {
      1: [1.06, -0.2],
      2: [1.04, -0.2],
      3: [1.02, 0.16],
      4: [1.04, 0.1],
      5: [1.13, -0.16],
      8: [1.03, 0.16],
    }
    const c = calottes[this.apparence.chapeau]
    if (!c) return { epaisseur: Infinity, bord: -1 }
    return { epaisseur: Math.max(0.006, c[0] - 1 - JEU_TISSU / R), bord: c[1] }
  }

  /**
   * Hauteur ou faire sortir un noeud de cheveux (chignon, elastique, tresse)
   * pour qu'il emerge SOUS la coiffe. `marge` est son propre rayon: c'est lui
   * qui traversait le chapeau, pas son point d'attache.
   */
  private sousBord(y: number, marge: number): number {
    const place = this.placeSousChapeau()
    if (place.epaisseur === Infinity) return y
    return Math.min(y, CY + R * place.bord - marge)
  }

  /**
   * Hauteur de la ligne de cheveux, par azimut, en y de sphere unite. Point
   * fixe: la limite depend de la direction, qui depend de la hauteur.
   *
   * On le cherche par DICHOTOMIE et non par iteration simple: `brut(y) - y`
   * decroit strictement, donc la coupe converge quels que soient les reglages,
   * alors que l'iteration divergeait des que `devant` depassait 1 — et rendait
   * alors une ligne qui sautait d'une rangee a l'autre.
   *
   * Le plancher n'est plus une constante. Devant le visage il remonte a la
   * ligne des sourcils, et sur les cotes il redescend au menton comme avant.
   * Aucune combinaison de parametres ne peut donc poser des cheveux sur les
   * yeux, ce que neuf reglages a la main ne sauraient promettre.
   */
  private bordCheveux(devant: number, cote: number, base: number): (a: number) => number {
    return (a: number): number => {
      const brut = (y: number): number => {
        const r = Math.sqrt(Math.max(0, 1 - y * y))
        const x = Math.sin(a) * r
        const z = Math.cos(a) * r
        return (
          base +
          devant * Math.pow(Math.max(0, z), 1.3) +
          cote * Math.pow(Math.max(0, Math.abs(x) - 0.45), 1.1) -
          0.09 * Math.max(0, -z) * bosse(x, 0, 0.7)
        )
      }
      let bas = -0.92
      let haut = 0.8
      // Douze coupes: la ligne est posee au demi-millimetre sur une tete de 19 cm.
      for (let k = 0; k < 12; k++) {
        const m = (bas + haut) / 2
        if (brut(m) > m) bas = m
        else haut = m
      }
      // z de la direction PRISE A HAUTEUR DE SOURCIL: le plancher ne doit pas
      // dependre de la ligne qu'il corrige, sinon il s'ouvre la ou il sert.
      const zSourcil = Math.cos(a) * Math.sqrt(1 - SOURCIL * SOURCIL)
      const plancher = -0.92 + (SOURCIL + 0.92) * smoothstep(0.29, 0.56, zSourcil)
      return clamp((bas + haut) / 2, plancher, 0.8)
    }
  }

  /**
   * Chevelure parametrique. On ne DECOUPE pas une sphere: on deplace ses
   * rangees pour que la derniere tombe pile sur la ligne de cheveux. Le bord de
   * la coiffure suit donc une courbe lisse au lieu de l'escalier qu'on obtenait
   * en seuillant une sphere a douze facettes.
   *
   * `forme(t, azimut)` donne l'epaisseur de cheveux, t = 0 au sommet du crane,
   * 1 a la ligne de cheveux, ou l'epaisseur doit valoir 0.
   */
  private coiffure(
    bord: (azimut: number) => number,
    forme: (t: number, azimut: number) => number,
    seg = 16,
  ): THREE.BufferGeometry {
    // La derniere rangee EST la ligne de cheveux, posee juste au-dessus de la
    // peau: le bord de la chevelure est donc une courbe nette, alors qu'en
    // laissant la coque traverser le crane il partait en dents de scie.
    const RANGEES = [0.94, 0.84, 0.7, 0.55, 0.38, 0.2, 0]
    const profil: [number, number][] = RANGEES.map((u) => [1, -u])
    const g = revolution(profil, seg, Math.PI)
    const pos = g.getAttribute('position') as THREE.BufferAttribute
    const v = new THREE.Vector3()
    const U = 0.94
    const place = this.placeSousChapeau()
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i)
      const azimut = Math.atan2(v.x, v.z)
      const u = clamp(-v.y, 0, 1)
      // On repartit les rangees en ANGLE POLAIRE depuis le sommet du crane, pas
      // en hauteur: sinon elles se tassent au bord et le sommet finit en cone.
      const theta = u * Math.acos(clamp(bord(azimut), -0.99, 0.985))
      const bordure = smoothstep(0.68, 0.94, u)
      const brut = forme(clamp(u / U, 0, 1), azimut) * (1 - bordure) + 0.026 * bordure
      // Sous une calotte fermee, la CHEVELURE se plaque. La contrainte suit la
      // hauteur et non la coiffure: au-dessus du bord du chapeau on plafonne,
      // en dessous on rend son volume, sinon coiffer un chapeau raserait aussi
      // la nuque et les cotes qui devraient continuer de depasser.
      const sous = smoothstep(place.bord - 0.3, place.bord + 0.06, Math.cos(theta))
      const e = brut + (Math.min(brut, place.epaisseur) - brut) * sous
      // On dilate TOUTE la direction, y compris y: sinon le sommet du crane
      // reste collee a la peau et la coiffure a l'air peinte dessus.
      v.set(Math.sin(azimut) * Math.sin(theta), Math.cos(theta), Math.cos(azimut) * Math.sin(theta)).multiplyScalar(1 + e)
      formerTete(v, this.dim.machoire)
      pos.setXYZ(i, v.x, v.y, v.z)
    }
    pos.needsUpdate = true
    g.computeVertexNormals()
    souderNormales(g)
    return g
  }

  private cheveux(as: Assemblage): void {
    const i = this.apparence.cheveux
    if (i === 0) return
    const ech: [number, number, number] = [R, R, R]
    const pose: [number, number, number] = [0, CY, 0]
    const place = this.placeSousChapeau()
    const couvert = place.epaisseur !== Infinity

    if (i === 1) {
      // Courts: une frange balayee sur le cote, des favoris devant l'oreille.
      // La frange est posee JUSTE au-dessus de la ligne de cheveux (t proche de
      // 1): centree au milieu du crane elle faisait une bosse sur le haut du
      // front, et ce n'est pas la que tombe une frange.
      as.ajouter(
        'cheveux',
        this.coiffure(
          this.bordCheveux(0.98, 0.62, -0.17),
          (t, a) => 0.1 * (1 - t * t) + 0.04 * bosse(t, 0.82, 0.42) * Math.max(0, Math.cos(a)) ** 2,
        ),
        pose,
        undefined,
        ech,
      )
      for (const s of [-1, 1]) {
        as.ajouter('cheveuxOmbre', capsule(0.012, 0.056, 1, 5), [s * R * 0.93, CY + 0.006, 0.042])
      }
    } else if (i === 2) {
      // Ondules: la masse est parcourue de vagues.
      as.ajouter(
        'cheveux',
        this.coiffure(
          this.bordCheveux(1.12, 0.55, -0.24),
          (t, a) => 0.115 * (1 - t * t) * (1 + 0.42 * Math.sin(a * 5 + t * 5)),
          18,
        ),
        pose,
        undefined,
        ech,
      )
    } else if (i === 3) {
      // Queue de cheval: masse tiree en arriere, elastique, queue qui retombe.
      as.ajouter(
        'cheveux',
        this.coiffure(this.bordCheveux(1.07, 0.5, -0.14), (t, a) => 0.07 * (1 - t * t) * (1 + 0.35 * Math.max(0, -Math.cos(a)))),
        pose,
        undefined,
        ech,
      )
      // Sous un chapeau, la queue est nouee plus bas: elle sort quand meme, par
      // derriere, ce qui est tout l'interet de la garder.
      const dq = this.sousBord(CY + 0.075, 0.045) - (CY + 0.075)
      as.ajouter('cheveuxOmbre', tore(0.032, 0.011, 3, 8), [0, CY + 0.075 + dq, -R * 0.92], [0.4, 0, 0])
      as.ajouter(
        'cheveux',
        courbe(
          [
            [0, CY + 0.05 + dq, -R * 0.95],
            [0.01, CY - 0.05 + dq, -R * 1.16],
            [0.015, CY - 0.17 + dq, -R * 1.08],
            [0.01, CY - 0.29 + dq, -R * 0.8],
          ],
          0.031,
          6,
          5,
        ),
      )
      as.ajouter('cheveux', sphere(0.023, 6, 5), [0.01, CY - 0.315 + dq, -R * 0.74])
    } else if (i === 4) {
      // Chignon: raie au milieu, masse lissee, chignon noue derriere.
      as.ajouter(
        'cheveux',
        this.coiffure(
          this.bordCheveux(1.07, 0.5, -0.08),
          (t, a) => 0.07 * (1 - t * t) * (1 - 0.5 * bosse(a, 0, 0.32) * (1 - t)),
        ),
        pose,
        undefined,
        ech,
      )
      // Le chignon est gros: sous une coiffe il ne peut pas se tasser, il
      // descend a la nuque. La marge vaut sa demi-hauteur, sinon c'est lui et
      // non son attache qui ressortirait par le dessus.
      const yc = this.sousBord(CY + 0.13, 0.075)
      const chignon = sphere(0.078, 8, 6)
      bosseler(chignon, 0.13, 5)
      as.ajouter('cheveux', chignon, [0, yc, -R * 0.72], undefined, [1.1, 0.85, 1])
      as.ajouter('cheveuxOmbre', tore(0.062, 0.014, 3, 8), [0, yc - 0.015, -R * 0.72], [1.25, 0, 0])
    } else if (i === 5) {
      // Longs: la masse descend dans le dos, deux meches tombent devant.
      as.ajouter('cheveux', this.coiffure(this.bordCheveux(1.28, 0.5, -0.42), (t) => 0.1 * (1 - t ** 3)), pose, undefined, ech)
      as.ajouter('cheveux', this.rideau(CY - 0.34, 1.06, 1.3, this.sousBord(CY + 0.15, 0.03)))
      // Les deux meches de devant partent du haut du crane: sous un chapeau
      // elles le traverseraient de part en part, et les descendre les ferait
      // pendre dans le torse. La longueur reste dite par le rideau.
      if (!couvert) {
        for (const s of [-1, 1]) {
          as.ajouter(
            'cheveux',
            courbe(
              [
                [s * R * 0.93, CY + 0.1, 0.02],
                [s * R * 1.0, CY - 0.02, 0.05],
                [s * R * 0.98, CY - 0.16, 0.04],
                [s * R * 0.9, CY - 0.3, -0.01],
              ],
              0.03,
              5,
              4,
            ),
          )
        }
      }
    } else if (i === 6) {
      // Nattes: masse courte, raie, deux tresses nouees.
      as.ajouter(
        'cheveux',
        this.coiffure(
          this.bordCheveux(1.1, 0.42, -0.2),
          (t, a) => 0.085 * (1 - t * t) * (1 - 0.4 * bosse(a, 0, 0.28) * (1 - t)),
        ),
        pose,
        undefined,
        ech,
      )
      const dn = this.sousBord(CY - 0.03, 0.035) - (CY - 0.03)
      for (const s of [-1, 1]) {
        as.ajouter('cheveuxOmbre', tore(0.024, 0.009, 3, 6), [s * R * 0.88, CY - 0.03 + dn, -0.02], [0, 0, s * 0.2])
        as.ajouter(
          'cheveux',
          courbe(
            [
              [s * R * 0.88, CY - 0.04 + dn, -0.02],
              [s * R * 0.95, CY - 0.14 + dn, -0.02],
              [s * R * 0.98, CY - 0.24 + dn, 0.0],
            ],
            0.027,
            5,
            4,
          ),
        )
        for (let k = 0; k < 2; k++) {
          as.ajouter('cheveuxClair', sphere(0.029, 5, 3), [
            s * R * (0.9 + k * 0.045),
            CY - 0.09 - k * 0.09 + dn,
            -0.015 + k * 0.01,
          ])
        }
      }
    } else if (i === 7) {
      // Crete: cotes rases plus une lame qui court du front a la nuque.
      as.ajouter(
        'cheveuxOmbre',
        this.coiffure(this.bordCheveux(1.46, 0.2, -0.42), (t) => 0.03 * (1 - 0.4 * t * t), 14),
        pose,
        undefined,
        ech,
      )
      // La lame monte a 1.3 rayon de tete: aucune calotte ne va si haut, elle
      // la traversait donc de part en part. Sous un chapeau elle se couche,
      // d'autant plus que la coiffe est serree.
      const rLame = place.epaisseur === Infinity ? 0.055 : clamp(place.epaisseur * R * 0.45, 0.004, 0.055)
      const relief =
        place.epaisseur === Infinity ? 1 : clamp((place.epaisseur - rLame / R) / 0.3, 0, 1)
      // Direction (y, z) en unites de crane, du front a la nuque.
      const LAME: readonly (readonly [number, number])[] = [
        [0.158, 0.9],
        [0.94, 0.62],
        [1.3, 0],
        [0.88, -0.66],
        [-0.316, -0.88],
      ]
      const pts: [number, number, number][] = LAME.map(([y, z]) => {
        const l = Math.hypot(y, z)
        const k = (1 + relief * (l - 1)) / l
        return [0, CY + R * y * k, R * z * k]
      })
      as.ajouter('cheveux', courbe(pts, rLame, 8, 6), undefined, undefined, [0.38, 1, 1])
    } else if (i === 8) {
      // Afro: une grosse masse ronde, irreguliere.
      const g = this.coiffure(this.bordCheveux(1.23, 0.3, -0.34), (t) => 0.44 * (1 - t * t * t), 18)
      // Les bosses valent un vingtieme du rayon: sous une coiffe elles suffisent
      // a repercer ce que le plafond d'epaisseur vient d'aplatir.
      bosseler(g, couvert ? 0.03 : 0.1, 11)
      as.ajouter('cheveux', g, [0, CY + 0.012, -0.006], undefined, ech)
    } else {
      // Carre: une masse nette qui s'arrete a la machoire, avec une frange. La
      // frange est plaquee contre le front, juste au-dessus de la ligne de
      // cheveux: plus bas et plus epaisse, elle faisait une visiere sur les yeux.
      as.ajouter(
        'cheveux',
        this.coiffure(
          this.bordCheveux(1.17, 0.36, -0.36),
          (t, a) => 0.09 * (1 - t * t) + 0.045 * bosse(t, 0.88, 0.38) * Math.max(0, Math.cos(a)) ** 3,
        ),
        pose,
        undefined,
        ech,
      )
      as.ajouter('cheveux', this.rideau(CY - 0.15, 1.02, 1.42, this.sousBord(CY + 0.15, 0.03)))
    }
  }

  /**
   * Nappe de cheveux qui retombe dans le dos. Revolution complete dont le
   * devant rentre sous la peau: la geometrie reste fermee, et il n'y a plus de
   * plaque plate de chaque cote du visage.
   *
   * `yHaut` est l'attache: sous un chapeau elle descend sous la coiffe, et le
   * profil se retasse au lieu de la traverser. Les paliers sont donc donnes en
   * FRACTION de la hauteur — ecrits en metres, ils se croisaient des que la
   * nappe raccourcissait, et une revolution a paliers desordonnes se replie.
   */
  private rideau(yBas: number, evase: number, arc: number, yHaut = CY + 0.15): THREE.BufferGeometry {
    const h = Math.max(yHaut, yBas + 0.12)
    const ourlet = Math.min(0.07, (h - yBas) * 0.25)
    const palier = (f: number): number => yBas + (h - yBas) * f
    const profil: [number, number][] = [
      [R * 0.52 * evase, yBas + ourlet * 0.45],
      [R * 0.78 * evase, yBas],
      [R * 0.9 * evase, yBas + ourlet],
      [R * 1.02, palier(0.35)],
      [R * 1.05, palier(0.65)],
      [R * 1.0, palier(0.84)],
      [R * 0.78, palier(1)],
    ]
    const g = revolution(profil, 16, Math.PI)
    sculpter(g, (v) => {
      const d = Math.hypot(v.x, v.z) || 1e-6
      const devant = smoothstep(0.2 + 0.8 * (1 - arc), 0.9, v.z / d)
      const f = (1 - 0.74 * devant) * (1 + 0.045 * Math.cos(Math.atan2(v.x, v.z) * 7))
      v.x *= f
      v.z *= f * 0.97
    })
    return g
  }

  // -------------------------------------------------------------------- barbe

  private barbe(as: Assemblage): void {
    const i = this.apparence.barbe
    if (i === 0) return

    const moustache = (): void => {
      for (const s of [-1, 1]) {
        const m = capsule(0.017, 0.062, 1, 5)
        as.ajouter('barbe', m, [s * 0.026, CY - 0.062, 0.172], [0.3, 0, s * 1.35], [1, 1, 0.7])
      }
    }
    const masse = (epais: number, bas: number) =>
      this.volumeTete((x, y, z) => {
        const b = smoothstep(0.05, -0.3, y)
        const devant = smoothstep(-0.42, 0.2, z)
        // On evite la bouche: sinon la barbe avale les levres.
        const trou = 1 - 0.85 * bosse(y, -0.46, 0.2) * bosse(x, 0, 0.42) * Math.max(0, z)
        return b * devant * trou * bas
      }, epais, 9, 7)

    if (i === 1) {
      moustache()
    } else if (i === 2) {
      // Bouc: moustache plus une touffe sous la levre.
      moustache()
      const bouc = sphere(0.042, 8, 6)
      sculpter(bouc, (v) => {
        v.x *= 1 - 0.5 * clamp(-v.y / 0.042, 0, 1)
        v.z *= 0.62
      })
      as.ajouter('barbe', bouc, [0, CY - 0.132, 0.135], undefined, [1, 1.15, 1])
      as.ajouter('barbeOmbre', sphere(0.026, 6, 4), [0, CY - 0.1, 0.158], undefined, [1.15, 0.6, 0.62])
    } else if (i === 3) {
      // Barbe courte: la masse epouse la machoire.
      as.ajouter('barbe', masse(0.075, 1), [0, CY, 0], undefined, [R, R, R])
      moustache()
    } else {
      // Grande barbe: la masse plus une pointe qui descend sur la poitrine.
      as.ajouter('barbe', masse(0.11, 1), [0, CY, 0], undefined, [R, R, R])
      moustache()
      const pointe = revolution(
        [
          [0.006, -0.3],
          [0.05, -0.24],
          [0.08, -0.14],
          [0.1, -0.05],
          [0.105, 0.02],
        ],
        8,
        Math.PI,
      )
      sculpter(pointe, (v) => {
        const a = Math.atan2(v.x, v.z)
        const f = 1 + 0.09 * Math.cos(a * 5)
        v.x *= f
        v.z *= f
        v.z = v.z * 0.9 + 0.02
      })
      as.ajouter('barbe', pointe, [0, CY - 0.075, 0.03])
    }
  }

  // ------------------------------------------------------------------ chapeau

  private chapeau(as: Assemblage): void {
    const i = this.apparence.chapeau
    if (i === 0) return
    const haut = CY + R

    /** Bord de chapeau: un disque epais, eventuellement partiel et tombant. */
    const bord = (
      rInt: number,
      rExt: number,
      y: number,
      ep: number,
      seg: number,
      phi0 = 0,
      phiL = TAU,
    ): THREE.BufferGeometry =>
      revolution(
        [
          [rInt, y],
          [rExt, y - 0.002],
          [rExt, y + ep],
          [rInt, y + ep + 0.002],
        ],
        seg,
        phi0,
        phiL,
      )

    if (i === 1) {
      // Tuque: maille bosselee, revers roule, pompon.
      const d = this.dome(0.56)
      bosseler(d, 0.05, 3)
      as.ajouter('chapeau', d, [0, CY, 0], undefined, [R * 1.09, R * 1.06, R * 1.09])
      const revers = tore(R * 1.0, 0.034, 4, 12)
      bosseler(revers, 0.14, 9)
      as.ajouter('chapeauMotif', revers, [0, CY - 0.018, 0], [Math.PI / 2, 0, 0], [1, 1, 0.95])
      const pompon = sphere(0.042, 6, 5)
      bosseler(pompon, 0.22, 4)
      as.ajouter('chapeauMotif', pompon, [0, CY + R * 1.06 + 0.024, 0])
    } else if (i === 2) {
      // Casquette: calotte, bandeau, visiere courbee, bouton.
      as.ajouter('chapeau', this.dome(0.56), [0, CY, 0], undefined, [R * 1.06, R * 1.04, R * 1.06])
      as.ajouter('chapeauOmbre', tore(R * 1.02, 0.013, 3, 10), [0, CY - 0.048, 0], [Math.PI / 2, 0, 0], [1, 1, 0.96])
      const visiere = bord(R * 0.85, R * 1.62, 0, 0.016, 12, -Math.PI * 0.34, Math.PI * 0.68)
      sculpter(visiere, (v) => {
        const t = clamp((Math.hypot(v.x, v.z) - R * 0.85) / (R * 0.77), 0, 1)
        v.y -= 0.05 * t * t
        v.z *= 0.94
      })
      as.ajouter('chapeau', visiere, [0, CY - 0.026, 0.01])
      as.ajouter('chapeauMotif', sphere(0.014, 5, 4), [0, CY + R * 1.04 + 0.006, 0])
    } else if (i === 3) {
      // Chapeau de paille: calotte tronconique, large bord ondule, ruban.
      as.ajouter(
        'chapeau',
        revolution(
          [
            [R * 1.02, CY + 0.03],
            [R * 0.99, CY + 0.11],
            [R * 0.9, CY + 0.17],
            [R * 0.62, CY + 0.198],
            [0.006, CY + 0.206],
          ],
          14,
          Math.PI,
        ),
      )
      const b = bord(R * 1.0, R * 1.92, CY + 0.048, 0.014, 12)
      sculpter(b, (v) => {
        const d = Math.hypot(v.x, v.z)
        const t = clamp((d - R) / (R * 0.92), 0, 1)
        const a = Math.atan2(v.x, v.z)
        v.y -= 0.055 * t * t + 0.016 * t * Math.cos(a * 5)
      })
      as.ajouter('chapeau', b)
      as.ajouter('chapeauMotif', tore(R * 1.03, 0.019, 3, 10), [0, CY + 0.082, 0], [Math.PI / 2, 0, 0])
    } else if (i === 4) {
      // Chapeau de cowboy: calotte pincee, bord releve sur les cotes, galon.
      const calotte = revolution(
        [
          [R * 1.04, CY + 0.02],
          [R * 0.95, CY + 0.12],
          [R * 0.86, CY + 0.2],
          [R * 0.55, CY + 0.232],
          [0.006, CY + 0.24],
        ],
        14,
        Math.PI,
      )
      sculpter(calotte, (v) => {
        // Le pli du sommet: on creuse le milieu sur l'axe avant-arriere.
        const t = smoothstep(CY + 0.1, CY + 0.24, v.y)
        v.y -= 0.05 * t * bosse(v.x, 0, R * 0.55)
        v.x *= 1 + 0.1 * t
      })
      as.ajouter('chapeau', calotte)
      const b = bord(R * 1.02, R * 1.75, CY + 0.035, 0.016, 14)
      sculpter(b, (v) => {
        const d = Math.hypot(v.x, v.z)
        const t = clamp((d - R) / (R * 0.75), 0, 1)
        // Retrousse sur les cotes, tombant devant et derriere.
        const cote = Math.abs(v.x) / Math.max(0.001, d)
        v.y += t * t * (0.07 * cote - 0.045 * (1 - cote))
        v.z *= 0.93
      })
      as.ajouter('chapeau', b)
      as.ajouter('chapeauMotif', tore(R * 1.05, 0.016, 3, 10), [0, CY + 0.062, 0], [Math.PI / 2, 0, 0])
      as.ajouter('metal', boite(0.026, 0.02, 0.01), [0, 0.062 + CY, R * 1.05])
    } else if (i === 5) {
      // Casque de chantier: coque, nervure, petit bord, jugulaire.
      as.ajouter('chapeau', this.dome(0.56), [0, CY + 0.01, 0], undefined, [R * 1.13, R * 1.14, R * 1.13])
      as.ajouter('chapeauMotif', boite(0.042, 0.03, R * 2.05, 1, 1, 3), [0, CY + R * 1.11, 0])
      const b = bord(R * 1.05, R * 1.42, CY - 0.03, 0.014, 12, -Math.PI * 0.44, Math.PI * 0.88)
      sculpter(b, (v) => {
        v.z *= 0.9
        v.y -= 0.012 * clamp((Math.hypot(v.x, v.z) - R) / (R * 0.4), 0, 1)
      })
      as.ajouter('chapeau', b)
      as.ajouter('sombre', tore(R * 1.06, 0.007, 3, 10), [0, CY - 0.03, 0], [Math.PI / 2, 0, 0], [1, 1, 0.95])
    } else if (i === 6) {
      // Couronne: bandeau, pointes, pierres.
      as.ajouter(
        'chapeau',
        revolution(
          [
            [R * 1.02, CY + 0.1],
            [R * 1.06, CY + 0.13],
            [R * 1.06, CY + 0.2],
            [R * 1.0, CY + 0.215],
          ],
          12,
          Math.PI,
        ),
      )
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * TAU
        as.ajouter(
          'chapeau',
          new THREE.ConeGeometry(0.03, 0.07, 6),
          [Math.sin(a) * R * 1.03, CY + 0.25, Math.cos(a) * R * 1.03],
        )
        as.ajouter('accessoireMotif', sphere(0.013, 4, 3), [Math.sin(a) * R * 1.09, CY + 0.165, Math.cos(a) * R * 1.09])
      }
    } else if (i === 7) {
      // Bandeau: bande de tissu, noeud et deux pans derriere.
      as.ajouter('chapeau', tore(R * 1.0, 0.026, 3, 12), [0, CY + 0.07, 0], [Math.PI / 2, 0, 0], [1, 1, 0.96])
      as.ajouter('chapeau', sphere(0.032, 6, 5), [0, CY + 0.07, -R * 1.02], undefined, [1, 0.9, 0.8])
      for (const s of [-1, 1]) {
        as.ajouter(
          'chapeauMotif',
          courbe(
            [
              [0, CY + 0.07, -R * 1.02],
              [s * 0.03, CY + 0.02, -R * 1.14],
              [s * 0.05, CY - 0.04, -R * 1.1],
            ],
            0.014,
            5,
            4,
          ),
        )
      }
    } else {
      // Beret: galette souple posee de travers, avec sa petite queue. Elle se
      // referme AU-DESSUS de `haut`: fermee plus bas, comme avant, elle passait
      // sous le sommet du crane et la tete ressortait par le dessus.
      const g = revolution(
        [
          [R * 1.03, CY + 0.03],
          [R * 1.26, CY + 0.055],
          [R * 1.22, CY + 0.12],
          [R * 0.9, haut + 0.005],
          [R * 0.46, haut + 0.03],
          [0.006, haut + 0.04],
        ],
        12,
        Math.PI,
      )
      sculpter(g, (v) => {
        v.x *= 1.02
        v.y += 0.02 * (v.x / R)
      })
      as.ajouter('chapeau', g, [0, 0, -0.008], [0, 0, 0.14])
      as.ajouter('chapeauMotif', capsule(0.008, 0.028, 1, 5), [0.02, haut + 0.048, -0.006])
    }
  }

  private lunettes(as: Assemblage): void {
    const f = this.formeYeux()
    const z = 0.176
    for (const s of [-1, 1]) {
      as.ajouter('accessoire', tore(f.r * 1.15, 0.007, 3, 10), [s * f.ex, CY + f.dy, z], undefined, [1.05, 0.95, 0.6])
      as.ajouter('reflet', boite(0.016, 0.004, 0.004), [s * f.ex - 0.012, CY + f.dy + 0.016, z + 0.004], [0, 0, 0.5])
      as.ajouter(
        'accessoire',
        boite(0.01, 0.008, 0.12),
        [s * (f.ex + f.r * 1.05), CY + f.dy + 0.012, z - 0.088],
        [0, s * 0.12, 0],
      )
    }
    as.ajouter('accessoire', boite(0.03, 0.007, 0.007), [0, CY + f.dy + 0.008, z])
  }

  // --------------------------------------------------------------------- bras

  /** Reglages de manche pour le haut porte. */
  private manche(): { longue: boolean; bas: number; marge: number; plus: number; role: Role } {
    const i = this.apparence.haut
    const table: readonly (readonly [boolean, number, number])[] = [
      [false, 1, 0], // camisole: bras nus
      [false, -0.14, 0.008], // tricot
      [true, 0, 0.01], // chemise
      [true, 0, 0.014], // capuchon
      [false, 1, 0], // salopette: bras nus
      [true, 0, 0.016], // veste
      [false, -0.1, 0.012], // tunique
      [true, 0, 0.026], // manteau
      [false, -0.15, 0.009], // raye
      [false, -0.11, 0.014], // blouse
      [true, 0, 0.012], // chemisier
    ]
    const e = table[i] ?? (table[1] as readonly [boolean, number, number])
    return { longue: e[0], bas: e[1], marge: 1.1, plus: e[2], role: 'haut' }
  }

  /** Profil d'un bras (rayon, y) dans l'espace de son pivot. */
  private profilBras(): Profil {
    return [
      [0.042, -0.284],
      [0.048, -0.2],
      [0.055, -0.09],
      [0.063, 0.016],
      [0.056, 0.056],
      [0.028, 0.078],
      [0.006, 0.087],
    ]
  }

  private profilAvantBras(): Profil {
    return [
      [0.033, -0.272],
      [0.036, -0.2],
      [0.043, -0.12],
      [0.049, -0.04],
      [0.05, 0.014],
      [0.04, 0.038],
      [0.006, 0.05],
    ]
  }

  private remplirBras(as: Assemblage, gauche: boolean): void {
    const s = gauche ? -1 : 1
    const p = this.profilBras()
    const bras = revolution(p, SEG_MEMBRE, Math.PI)
    sculpter(bras, (v) => {
      // Biceps devant, epaule qui s'arrondit vers l'exterieur.
      if (v.z > 0) v.z += 0.008 * bosse(v.y, -0.11, 0.16)
      v.x += s * 0.012 * bosse(v.y, 0.03, 0.12)
    })
    as.ajouter('peau', bras)

    const m = this.manche()
    if (m.bas < 0.5) {
      const y0 = m.longue ? -0.286 : m.bas
      const profil: [number, number][] = []
      const r = (y: number): number => rayonCoque(p, y, m.marge, m.plus)
      if (!m.longue) {
        // La levre de l'ourlet reste HORS de la peau: c'est elle qui laissait
        // un lisere clair tout autour du bord de la manche courte.
        profil.push([horsPeau(p, y0 + 0.014, r(y0) * 0.92), y0 + 0.014])
        profil.push([r(y0) + 0.008, y0])
        profil.push([horsPeau(p, y0 + 0.026, r(y0 + 0.02) + 0.003), y0 + 0.026])
      } else {
        profil.push([horsPeau(p, y0, r(y0) - 0.004), y0])
      }
      for (const y of paliersCoque(p, y0 + 0.05, 0.05, 0.05)) profil.push([r(y), y])
      profil.push([r(0.062), 0.062])
      profil.push([r(0.08), 0.08])
      profil.push([0.006, 0.09])
      const g = revolution(profil, SEG_MEMBRE, Math.PI)
      sculpter(g, (v) => {
        v.x += s * 0.014 * bosse(v.y, 0.03, 0.13)
        if (v.z > 0) v.z += 0.006 * bosse(v.y, -0.11, 0.18)
      })
      as.ajouter(m.role, g)
      if (this.apparence.haut === 7) {
        // Manteau: la manche est matelassee elle aussi.
        for (const y of [-0.05, -0.16]) {
          as.ajouter('hautOmbre', tore(rayonCoque(p, y, m.marge, m.plus), 0.008, 3, 8), [0, y, 0], [Math.PI / 2, 0, 0])
        }
      }
    }
  }

  private remplirAvantBras(as: Assemblage, gauche: boolean): void {
    const p = this.profilAvantBras()
    const av = revolution(p, SEG_MEMBRE, Math.PI)
    sculpter(av, (v) => {
      // L'avant-bras se courbe legerement vers l'avant: moins baton.
      v.z += 0.03 * Math.pow(clamp((0.05 - v.y) / 0.32, 0, 1), 2)
      if (v.z > 0) v.z += 0.005 * bosse(v.y, -0.06, 0.14)
    })
    as.ajouter('peau', av)
    void gauche

    const m = this.manche()
    if (!m.longue) return
    const y0 = -0.2
    const r = (y: number): number => rayonCoque(p, y, m.marge, m.plus)
    const profil: [number, number][] = [
      [horsPeau(p, y0 + 0.016, r(y0) * 0.9), y0 + 0.016],
      [r(y0) + 0.012, y0 + 0.002],
      [horsPeau(p, y0 + 0.03, r(y0 + 0.03) + 0.004), y0 + 0.03],
    ]
    for (const y of paliersCoque(p, -0.1, 0.05, 0.08)) profil.push([r(y), y])
    profil.push([r(0.05), 0.052])
    const g = revolution(profil, SEG_MEMBRE, Math.PI)
    sculpter(g, (v) => {
      v.z += 0.03 * Math.pow(clamp((0.05 - v.y) / 0.32, 0, 1), 2)
    })
    as.ajouter(m.role, g)
    as.ajouter('hautOmbre', tore(r(y0) + 0.006, 0.009, 3, 8), [0, y0 + 0.03, 0.012], [Math.PI / 2, 0, 0])
  }

  // --------------------------------------------------------------------- main

  private remplirMain(as: Assemblage, gauche: boolean): void {
    // Le pouce est du cote interieur: +x a gauche, -x a droite.
    const inX = gauche ? 1 : -1

    const paume = boite(0.07, 0.088, 0.042, 2, 2, 1)
    sculpter(paume, (v) => {
      const ry = Math.abs(v.y) / 0.044
      const rx = Math.abs(v.x) / 0.035
      v.z *= 1 - 0.28 * ry * ry - 0.18 * rx * rx
      v.x *= 1 - 0.16 * ry * ry
      // Le dos de la main est bombe, la paume creuse.
      if (v.z < 0) v.z -= 0.004
      else v.z += 0.004 * (1 - rx * rx)
    })
    as.ajouter('peau', paume, [inX * 0.004, -0.048, 0.002])

    const doigts: readonly [number, number, number][] = [
      [-0.027, 0.052, -0.16],
      [-0.009, 0.06, -0.1],
      [0.009, 0.058, -0.08],
      [0.027, 0.046, -0.2],
    ]
    for (const d of doigts) {
      const x = inX * d[0]
      as.ajouter('peau', capsule(0.0142, d[1], 1, 4), [x, -0.092 - d[1] / 2 + 0.014, 0.012], [d[2], 0, inX * d[0] * 1.6])
    }
    // Pouce: decale sur le cote, oppose aux autres doigts.
    as.ajouter('peau', capsule(0.0165, 0.056, 1, 5), [inX * 0.04, -0.052, 0.026], [-0.5, 0, inX * 1.05])
    as.ajouter('peau', sphere(0.017, 4, 3), [inX * 0.031, -0.03, 0.014])
    // Barre des jointures: elle raccorde les doigts a la paume.
    as.ajouter('peau', boite(0.064, 0.022, 0.032), [inX * 0.004, -0.089, 0.01])
  }

  // -------------------------------------------------------------------- jambe

  private remplirJambe(as: Assemblage, gauche: boolean): void {
    const s = gauche ? -1 : 1
    const p = this.dim.jambe
    const jambe = revolution(p, SEG_MEMBRE, Math.PI)
    sculpter(jambe, (v) => {
      // Mollet en arriere, rotule devant, cuisse un peu plus pleine dehors.
      if (v.z < 0) v.z -= 0.016 * bosse(v.y, -0.48, 0.16)
      if (v.z > 0) v.z += 0.008 * bosse(v.y, -0.36, 0.08) + 0.008 * bosse(v.y, -0.12, 0.2)
      v.x += s * 0.008 * bosse(v.y, -0.05, 0.14)
    })
    as.ajouter('peau', jambe)

    // Longueur de jambe du vetement, par piece du bas.
    const bas = this.apparence.bas
    const table: readonly (readonly [number, number, number])[] = [
      [-0.655, 1.28, 0.012],
      [-0.63, 1.3, 0.013],
      [-0.28, 1.34, 0.014],
      [-0.655, 1.32, 0.014],
      [1, 0, 0],
      [1, 0, 0],
      [-0.685, 1.06, 0.004],
      [-0.36, 1.36, 0.013],
    ]
    const e = table[bas] ?? (table[0] as readonly [number, number, number])
    if (e[0] < 0.5) {
      const y0 = e[0]
      const r = (y: number): number => this.rayonJambe(y, e[1], e[2])
      const profil: [number, number][] = [
        [horsPeau(p, y0 + 0.016, r(y0) * 0.86), y0 + 0.016],
        [r(y0) + 0.009, y0],
        [horsPeau(p, y0 + 0.03, r(y0 + 0.03) + 0.003), y0 + 0.03],
      ]
      for (const y of paliersCoque(p, y0 + 0.11, 0.0, 0.12)) profil.push([r(y), y])
      profil.push([r(0.03), 0.03])
      profil.push([r(0.06), 0.062])
      const g = revolution(profil, SEG_MEMBRE, Math.PI)
      sculpter(g, (v) => {
        // Un pli marque au genou et un autre a la cheville: le tissu casse.
        const f = 1 + 0.045 * bosse(v.y, -0.34, 0.06) + 0.03 * bosse(v.y, y0 + 0.09, 0.07)
        v.x *= f
        v.z *= f
        if (v.z < 0) v.z -= 0.01 * bosse(v.y, -0.48, 0.18)
      })
      as.ajouter('bas', g)
      if (bas === 1) {
        as.ajouter(
          'basClair',
          revolution(
            [
              [r(y0) + 0.008, y0 + 0.004],
              [r(y0 + 0.025) + 0.014, y0 + 0.026],
              [r(y0 + 0.05) + 0.005, y0 + 0.052],
            ],
            SEG_MEMBRE,
            Math.PI,
          ),
        )
      }
      if (bas === 3) {
        as.ajouter('basOmbre', boite(0.088, 0.085, 0.01), [0, -0.34, rayonCoque(p, -0.34, e[1], e[2]) + 0.004])
      }
      if (bas === 7) {
        for (let k = 0; k < 4; k++) {
          const a = (k / 4) * TAU + 0.5
          const y = -0.14 - (k % 2) * 0.12
          const rr = rayonCoque(p, y, e[1], e[2]) + 0.004
          as.ajouter('basMotif', sphere(0.015, 4, 3), [Math.sin(a) * rr, y, Math.cos(a) * rr])
        }
      }
    }

    // Tige de botte: elle appartient a la jambe, pas au pied, sinon elle
    // pivoterait avec la cheville.
    const so = this.apparence.souliers
    if (so === 3 || so === 4) {
      const h = so === 3 ? 0.27 : 0.17
      const tige = revolution(
        [
          [0.062, -0.7],
          [0.066, -0.7 + h * 0.5],
          [0.068, -0.7 + h],
          [0.06, -0.7 + h + 0.012],
        ],
        SEG_MEMBRE,
        Math.PI,
      )
      as.ajouter('soulier', tige)
      as.ajouter('soulierOmbre', anneau(0.06, 0.071, -0.7 + h - 0.03, -0.7 + h, SEG_MEMBRE))
      if (so === 4) {
        as.ajouter('metal', anneau(0.062, 0.07, -0.63, -0.618, SEG_MEMBRE))
      }
    }
  }

  // --------------------------------------------------------------------- pied

  private remplirPied(as: Assemblage, gauche: boolean): void {
    const p = PROP
    const sol = -p.piedHauteur
    const zPied = p.piedLongueur * 0.2
    const i = this.apparence.souliers
    const s = gauche ? -1 : 1

    /** Forme de pied ou de chaussure: une boite sculptee. */
    const forme = (l: number, h: number, d: number, orteils: number): THREE.BufferGeometry => {
      const g = boite(l, h, d, 2, 1, 3)
      sculpter(g, (v) => {
        const t = clamp(v.z / d + 0.5, 0, 1)
        const rx = Math.abs(v.x) / (l / 2)
        v.x *= 0.62 + 0.42 * bosse(t, 0.68, 0.7)
        if (v.y > 0) v.y = (h * 0.5 - h * orteils * Math.pow(t, 1.4)) * (1 - 0.3 * rx * rx)
        else v.y = -h * 0.5 + h * 0.12 * bosse(t, 0.45, 0.34) * (1 - rx)
        if (t > 0.78) v.z -= d * 0.16 * rx * rx * ((t - 0.78) / 0.22)
        if (t < 0.14) v.z += d * 0.12 * rx * rx * ((0.14 - t) / 0.14)
        // Le pied s'ecarte legerement vers l'exterieur.
        v.x += s * 0.004 * t
      })
      return g
    }

    if (i === 0) {
      // Pieds nus: cou-de-pied, talon, trois orteils suggeres, ongles clairs.
      as.ajouter('peau', forme(p.piedLargeur, p.piedHauteur * 1.9, p.piedLongueur, 0.62), [0, sol + p.piedHauteur * 0.95 - 0.008, zPied])
      for (let k = 0; k < 3; k++) {
        const x = s * (0.028 - k * 0.024)
        as.ajouter('peau', sphere(0.014 - k * 0.002, 5, 3), [x, sol + 0.014, zPied + p.piedLongueur * 0.44])
      }
      as.ajouter('peauOmbre', sphere(0.03, 5, 4), [0, sol + 0.026, zPied - p.piedLongueur * 0.42], undefined, [1, 1, 0.8])
      return
    }

    if (i === 5) {
      // Sandales: semelle fine, deux brides, le pied reste visible.
      as.ajouter('peau', forme(p.piedLargeur, p.piedHauteur * 1.85, p.piedLongueur, 0.6), [0, sol + p.piedHauteur * 0.92 - 0.006, zPied])
      as.ajouter('semelle', forme(p.piedLargeur * 1.16, 0.022, p.piedLongueur * 1.05, 0.05), [0, sol + 0.011, zPied])
      as.ajouter('soulier', forme(p.piedLargeur * 1.14, 0.014, p.piedLongueur * 1.03, 0.05), [0, sol + 0.03, zPied])
      for (const dz of [0.055, -0.045]) {
        as.ajouter('soulier', boite(p.piedLargeur * 1.2, 0.015, 0.028), [0, sol + 0.05, zPied + dz], [0, 0, 0])
      }
      as.ajouter('soulier', capsule(0.008, 0.07, 1, 5), [s * 0.012, sol + 0.05, zPied + 0.03], [0.9, 0, 0])
      return
    }

    // Chaussures fermees: semelle, tige, details selon le modele.
    const epaisSemelle = i === 2 ? 0.03 : i === 4 ? 0.028 : 0.02
    as.ajouter('semelle', forme(p.piedLargeur * 1.2, epaisSemelle, p.piedLongueur * 1.09, 0.04), [0, sol + epaisSemelle / 2, zPied])
    if (i === 2) {
      as.ajouter('blanc', forme(p.piedLargeur * 1.21, 0.014, p.piedLongueur * 1.1, 0.04), [0, sol + epaisSemelle - 0.004, zPied])
    }
    const hTige = p.piedHauteur * (i === 3 || i === 4 ? 1.85 : 1.7)
    as.ajouter('soulier', forme(p.piedLargeur * 1.14, hTige, p.piedLongueur * 1.04, 0.6), [0, sol + epaisSemelle + hTige * 0.5 - 0.012, zPied])

    if (i === 1) {
      // Souliers de ville: bout verni, languette, lacets croises.
      as.ajouter('soulierOmbre', forme(p.piedLargeur * 1.1, 0.03, p.piedLongueur * 0.4, 0.2), [0, sol + epaisSemelle + 0.03, zPied + p.piedLongueur * 0.32])
      as.ajouter('sombre', boite(0.038, 0.018, 0.05), [0, sol + hTige * 0.85, zPied + 0.028], [-0.35, 0, 0])
      for (let k = 0; k < 2; k++) {
        as.ajouter('blanc', boite(0.05, 0.006, 0.008), [0, sol + hTige * 0.9 - k * 0.014, zPied + 0.012 - k * 0.02])
      }
    } else if (i === 2) {
      // Espadrilles: bande laterale, languette rembourree, lacets.
      for (const sx of [-1, 1]) {
        as.ajouter('blanc', boite(0.012, 0.03, 0.1), [sx * p.piedLargeur * 0.56, sol + epaisSemelle + 0.026, zPied + 0.01], [0, 0, sx * 0.25])
      }
      as.ajouter('soulierOmbre', boite(0.042, 0.026, 0.055), [0, sol + hTige * 0.92, zPied + 0.012], [-0.3, 0, 0])
      for (let k = 0; k < 3; k++) {
        as.ajouter('blanc', boite(0.046, 0.005, 0.007), [0, sol + hTige * 0.95 - k * 0.013, zPied + 0.026 - k * 0.019])
      }
    } else if (i === 3) {
      // Bottes de pluie: coque lisse, bourrelet a la cheville.
      as.ajouter('soulierOmbre', tore(0.056, 0.01, 3, 6), [0, sol + hTige, zPied - 0.03], [Math.PI / 2, 0, 0], [1.1, 1, 1])
    } else {
      // Bottes de travail: renfort de bout, oeillets, lacets.
      as.ajouter('soulierOmbre', forme(p.piedLargeur * 1.13, 0.035, p.piedLongueur * 0.42, 0.25), [0, sol + epaisSemelle + 0.032, zPied + p.piedLongueur * 0.3])
      for (let k = 0; k < 2; k++) {
        for (const sx of [-1, 1]) {
          as.ajouter('metal', sphere(0.005, 3, 3), [sx * 0.022, sol + hTige * 0.78 - k * 0.024, zPied - 0.006 - k * 0.03])
        }
        as.ajouter('blanc', boite(0.05, 0.005, 0.007), [0, sol + hTige * 0.78 - k * 0.024, zPied - 0.006 - k * 0.03])
      }
    }
  }
}

// Partages par tous les apercus, donc jamais liberes: `dispose` du bonhomme
// n'a pas a s'en occuper.
let socleGeo: THREE.CylinderGeometry | null = null
let socleMat: THREE.MeshLambertMaterial | null = null

/**
 * Bonhomme sur un petit socle rond, tourne pour faire face a la camera du jeu
 * (azimut 45 degres). Passe `angle` si l'apercu utilise une autre camera.
 */
export function creerApercu(
  apparence: Apparence,
  angle = CAM_AZIMUTH,
): { groupe: THREE.Group; bonhomme: Bonhomme } {
  const groupe = new THREE.Group()
  const bonhomme = new Bonhomme(apparence)
  bonhomme.racine.rotation.y = angle
  socleGeo ??= new THREE.CylinderGeometry(PLAYER_RADIUS * 1.8, PLAYER_RADIUS * 1.95, 0.06, 24)
  socleMat ??= new THREE.MeshLambertMaterial({ color: 0x6f7a86 })
  const socle = new THREE.Mesh(socleGeo, socleMat)
  socle.position.y = -0.03
  socle.receiveShadow = true
  groupe.add(socle, bonhomme.racine)
  return { groupe, bonhomme }
}
